import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { MastersOpsPanel } from './MastersOpsPanel';
import { MastersBannerNotifications } from './MastersBannerNotifications';
import { DiscoveryConnectionMaster } from './DiscoveryConnectionMaster';
import { ResponseConnectionMaster } from './ResponseConnectionMaster';
import { GuardianMaster } from './GuardianMaster';
import { NeoMaster } from './NeoMaster';
import { fmtTs } from '../lib/globalTz';

type PolicyKey =
  | 'roleTemplates'
  | 'kqlTablePolicies'
  | 'kqlTableCatalog'
  | 'reportAccess'
  | 'featureFlags'
  | 'capabilityPermissions';

interface RoleTemplate {
  id: string;
  label: string;
  description?: string;
  color?: string;
}

interface KqlRolePolicy {
  mode?: 'allow_all_catalog' | 'selected' | string;
  allowedTables?: string[] | '*' | unknown;
  deniedTables?: string[] | unknown;
}

interface ReportRolePolicy {
  daily?: boolean;
  weekly?: boolean;
  monthly?: boolean;
  quarterly?: boolean;
  yearly?: boolean;
  saveMonthlySnapshot?: boolean;
  downloadHtml?: boolean;
}

interface CapabilityDef {
  id: string;
  label: string;
  description?: string;
  dangerous?: boolean;
}

interface CapabilityModuleDef {
  id: string;
  label: string;
  description: string;
  capabilities: CapabilityDef[];
}

type CapabilityPermissions = Record<string, Record<string, string[]>>;

interface CapabilityPermissionsResponse {
  catalog: {
    modules: CapabilityModuleDef[];
  };
  permissions: CapabilityPermissions;
  defaults: CapabilityPermissions;
  updatedBy?: string | null;
  updatedByEmail?: string | null;
  updatedAt?: string | null;
}

interface MastersPoliciesResponse {
  policies: Record<PolicyKey, unknown>;
  catalog: {
    tabs: { id: string; label: string; hint: string }[];
    tabIds: string[];
    kqlTables: string[];
    customKqlTables?: string[];
    capabilityModules?: CapabilityModuleDef[];
  };
}

interface WatchedDomainCustomer {
  id: string;
  name: string;
  workspaceName?: string;
  tenantId: string;
  enabled: boolean;
  neoEnabled: boolean;
  monitoredDomains: string[];
  neoLastRunAt?: string | null;
  neoLastRunSummary?: {
    totalUnacked?: number;
    newCount?: number;
    bySeverity?: Record<string, number>;
    bySource?: Record<string, number>;
  } | null;
}

interface WatchedDomainsResponse {
  customers: WatchedDomainCustomer[];
}

interface WatchedDomainsUpdateResponse {
  ok: boolean;
  customerId: string;
  monitoredDomains: string[];
  removedDomains: string[];
  acknowledgedFindings: number;
}

interface PlaybookAccessItem {
  id: string;
  customerId: string;
  customerName?: string | null;
  subscriptionId?: string | null;
  resourceGroup?: string | null;
  workflowName: string;
  resourceId: string;
  state?: string | null;
  triggerType?: string | null;
  description?: string | null;
  connectors?: string[];
  policyId?: string | null;
  enabled: boolean;
  allowedRoles: string[];
  allowedUsers: string[];
  policyUpdatedAt?: string | null;
  updatedByEmail?: string | null;
}

interface MastersCustomerSummary {
  id: string;
  name: string;
  subscription?: string;
  resourceGroup?: string;
  tenantId?: string;
  enabled?: boolean;
  lighthouseMode?: boolean;
}

interface PlaybookAccessResponse {
  ok: boolean;
  customers: MastersCustomerSummary[];
  playbooks: PlaybookAccessItem[];
}

interface PlaybookAccessDraft {
  enabled: boolean;
  allowedRoles: string[];
  allowedUsersText: string;
}

interface SocAssignmentItem {
  id?: string | null;
  customerId: string;
  userId?: string;
  displayName: string;
  userEmail: string;
  role: string;
  enabled: boolean;
  canAssign: boolean;
  autoAssign: boolean;
  allowServicePrincipalFallback: boolean;
  severityScopes: string[];
  source: string;
  lighthouseVerified: boolean;
  lastVerifiedAt?: string | null;
  notes?: string | null;
  updatedByEmail?: string | null;
  updatedAt?: string | null;
  portalCustomerAccess: boolean;
  hasAssignCapability: boolean;
  eligible: boolean;
  inRoster: boolean;
  reason?: string | null;
}

interface SocAssignmentLighthousePrincipalItem {
  id?: string | null;
  customerId: string;
  principalId: string;
  principalType: 'group' | 'user' | 'servicePrincipal' | 'unknown' | string;
  displayName?: string | null;
  enabled: boolean;
  source: string;
  notes?: string | null;
  deletedAt?: string | null;
  deletedByEmail?: string | null;
  updatedByEmail?: string | null;
  updatedAt?: string | null;
}

interface SocAssignmentLighthouseScopeItem {
  id?: string | null;
  customerId: string;
  scopeType: 'subscription' | 'resourceGroup' | string;
  subscriptionId: string;
  resourceGroup?: string | null;
  displayName?: string | null;
  enabled: boolean;
  notes?: string | null;
  lastCheckedAt?: string | null;
  lastResult?: string | null;
  deletedAt?: string | null;
  deletedByEmail?: string | null;
  updatedByEmail?: string | null;
  updatedAt?: string | null;
}

interface SocAssignmentResponse {
  ok: boolean;
  customers: MastersCustomerSummary[];
  assignees: SocAssignmentItem[];
  lighthousePrincipals?: SocAssignmentLighthousePrincipalItem[];
  lighthouseScopes?: SocAssignmentLighthouseScopeItem[];
  msspTenantId?: string | null;
  msspClientId?: string | null;
  updatedAt?: string | null;
}

interface SocAssignmentLighthouseResponse {
  ok: boolean;
  customerId: string;
  customerName?: string;
  subscriptionId?: string;
  status: 'verified' | 'partial' | 'unavailable' | 'not_applicable_mssp_home' | string;
  message: string;
  checkedAt: string;
  principalsDiscovered: number;
  groupsDiscovered: number;
  groupsExpanded: number;
  usersDiscovered: number;
  verifiedUsers: number;
  unverifiedUsers: number;
  updatedPolicies: number;
  warnings: string[];
  verifiedEmails?: string[];
  configuredPrincipalsUsed?: number;
  discoveredPrincipalsUsed?: number;
  verificationSource?: 'azure_lighthouse' | 'configured_principals' | 'hybrid' | 'not_applicable_mssp_home' | 'none' | string;
  tenantMode?: 'mssp_home_tenant' | 'lighthouse_customer' | string;
  scopesChecked?: number;
  resourceGroupScopesChecked?: number;
  technicalDetails?: string[];
}

interface SocAssignmentLighthousePrincipalDraft {
  enabled: boolean;
  principalType: 'group' | 'user' | 'servicePrincipal' | 'unknown' | string;
  displayName: string;
  notes: string;
}

interface SocAssignmentLighthouseScopeDraft {
  enabled: boolean;
  scopeType: 'subscription' | 'resourceGroup' | string;
  subscriptionId: string;
  resourceGroup: string;
  displayName: string;
  notes: string;
}

interface SocAssignmentDraft {
  enabled: boolean;
  canAssign: boolean;
  autoAssign: boolean;
  allowServicePrincipalFallback: boolean;
  severityScopes: string[];
  lighthouseVerified: boolean;
  notes: string;
}

const ASSIGNMENT_SEVERITY_OPTIONS = ['Any', 'High', 'Medium', 'Low', 'Informational'];

const POLICY_TITLES: Record<PolicyKey, string> = {
  roleTemplates: 'Role Master',
  kqlTablePolicies: 'KQL Table Access Master',
  kqlTableCatalog: 'KQL Global Table Catalog',
  reportAccess: 'Report Access Master',
  featureFlags: 'Feature Flags',
  capabilityPermissions: 'Permissions Master',
};

const FEATURE_LABELS: Record<string, string> = {
  advanced_hunting: 'Advanced Hunting',
  advanced_hunting_kql_intellisense: 'Advanced Hunting KQL IntelliSense',
  advanced_hunting_runtime_schema: 'Advanced Hunting Runtime Schema Autocomplete',
  advanced_hunting_query_builder: 'Advanced Hunting KQL Forge Builder',
  ai_assistant: 'AI Assistant',
  deep_investigation: 'Deep Investigation',
  threat_intel_lookup: 'Threat Intel Lookup',
  vulnerability_mgmt: 'Vulnerability Management',
  entity_360: 'Entity 360',
  soc_digital_twin: 'SOC Digital Twin',
  soc_debt_dashboard: 'SOC Debt Dashboard',
  ai_report_builder: 'AI-Powered Report Builder',
  incident_autopilot: 'SOC Copilot Autopilot',
  customer_executive_portal: 'Customer Executive Portal',
  next_best_query: 'AI Next Best Query',
  customer_update_generator: 'One-Click Customer Update Generator',
  analyst_workbench: 'Analyst Workbench / Guided Triage',
  incident_vulnerability_correlation: 'Vulnerability-to-Incident Correlation',
  incident_similarity_search: 'Incident Similarity Search',
  darkweb_scan: 'External Exposure Center',
  spiderfoot_scan: 'SpiderFoot Scan',
  dnstwist_scan: 'Phishing Domain Scanner',
  destructive_actions: 'Destructive Incident Actions',
  monthly_snapshot_resave: 'Monthly Snapshot Save/Re-save',
  case_evidence: 'Case Evidence',
  security_copilot: 'Security Copilot',
  invite_email_delivery: 'Invite Email Delivery',
  role_color_branding: 'Role Color Branding',
  customer_logo_branding: 'Customer Logo Branding',
  connector_health_guardian: 'Connector Health Guardian',
  detection_quality_score: 'Detection Quality Score',
  log_master: 'Log Master',
  ml_insights_implicit_feedback: 'ML Insights Implicit Feedback',
  ml_insights_llm_pseudo_feedback: 'ML Insights LLM Pseudo Feedback',
  ml_insights_training_dashboard: 'ML Insights Training Dashboard',
  ml_insights_scheduled_feedback_enrichment: 'ML Insights Scheduled AI Feedback Enrichment',
  mitre_defend_coverage: 'MITRE Coverage Command Center',
  oob_coverage_assessment: 'AnalyticsOps',
  oob_coverage_table_scan: 'AnalyticsOps Table-Based Extraction',
  oob_coverage_workspace_table_scan: 'AnalyticsOps Workspace Table Discovery',
  oob_coverage_ai_analysis: 'AnalyticsOps AI Analysis',
  oob_coverage_manifest_deployment: 'AnalyticsOps Manifest Deployment',
  oob_coverage_manifest_enabled_deployment: 'AnalyticsOps Manifest Enabled Deployment',
  oob_coverage_manifest_deployment_summary: 'AnalyticsOps Manifest Deployment Summary Export',
  oob_coverage_catalog: 'Azure-Sentinel OOB Catalog',
  oob_coverage_readiness: 'OOB Customer Readiness Mapping',
  oob_coverage_deployment: 'OOB Deployment Planner',
  oob_coverage_drift: 'OOB Drift & Recommended State',
  custom_analytics_studio: 'Custom Analytics Studio',
  custom_analytics_datasource_profiler: 'Custom Analytics Datasource Profiler',
  custom_analytics_ai_generation: 'Custom Analytics AI Rule Generation',
  custom_analytics_rule_review: 'Custom Analytics Rule Review Grid',
  custom_analytics_validation_quality: 'Custom Analytics Validation & Quality Scoring',
  custom_analytics_arm_yaml_preview: 'Custom Analytics KQL/YAML/ARM Preview',
  custom_analytics_deployment: 'Custom Analytics Deployment',
  custom_analytics_enabled_deployment: 'Custom Analytics Enabled Deployment',
  custom_analytics_update_existing: 'Custom Analytics Update Existing',
  custom_analytics_versioning_drift: 'Custom Analytics Versioning & Drift',
  custom_analytics_improvement_loop: 'Custom Analytics AI Improvement Loop',
  custom_analytics_feedback_links: 'Custom Analytics Quality / Debt / ML Feedback Links',
  custom_analytics_multi_table_profile: 'Custom Analytics Multi-table Profiling',
  custom_analytics_reference_url_import: 'Custom Analytics Reference URL Import',
  custom_analytics_rule_editing: 'Custom Analytics Rule KQL/ARM Editing',
  custom_analytics_ai_rectification: 'Custom Analytics AI KQL Rectification',
  custom_analytics_json_export: 'Custom Analytics JSON Export',
  custom_analytics_kloudsecure_repo: 'Custom Analytics KloudSecure Repo',
  custom_analytics_repository_import: 'Custom Analytics Repository Import',
  custom_analytics_repository_export: 'Custom Analytics Repository Export',
  custom_analytics_repository_operations: 'Custom Analytics Repository Operations',
  custom_analytics_repository_deployment: 'Custom Analytics Repository Deployment',
  custom_analytics_repository_enabled_deployment: 'Custom Analytics Repository Enabled Deployment',
  custom_analytics_repository_update_existing: 'Custom Analytics Repository Update Existing',
  custom_analytics_repository_malware_scan: 'Custom Analytics Repository Malware Scan',
  custom_analytics_repository_validation_override: 'Custom Analytics Repository Manual Validation Override',
  discovery_broker_native: 'Native Discovery Broker',
  estate_discovery_runtime: 'Estate Discovery Runtime (Stage 2D.3-A)',
  response_broker_native: 'Native Response Broker',
  response_native_actions: 'Native Entity Response Actions',
  response_commander_action_contract: 'Response Commander Action Contract (Stage 3B.6-C)',
  response_commander_stage3c: 'Response Commander (Stage 3C)',
  response_guard_stage4: 'Independent Judge + Policy Guard (Stage 4)',
  guardian_soc_stage5: 'Guardian SOC Control Plane (Stage 5A/5B)',
  guardian_observer_stage5c: 'Guardian Observer Orchestrator (Stage 5C)',
  guardian_operations_stage5d: 'Guardian Operations & Flight Recorder (Stage 5D)',
  neo_control_plane_stage5b1: 'Neo Control Plane (Stage 5B.1)',
  responseops_subscription_playbook_discovery: 'ResponseOps Subscription Playbook Discovery',
  playbook_access_master: 'Playbook Access Master',
  incident_assignment_roster: 'Customer-scoped Incident Assignment Roster',
  soc_assignment_master: 'SOC Assignment Master',
  soc_assignment_lighthouse_verification: 'SOC Assignment Azure Lighthouse Verification',
  soc_assignment_lighthouse_principal_registry: 'SOC Assignment MSSP Lighthouse Principal Registry',
  soc_assignment_lighthouse_scope_registry: 'SOC Assignment Lighthouse Delegated Scope Registry',
  huntops_workbooks: 'HuntOps Workbooks',
  workbooks_oob_catalog: 'Workbooks OOB Sentinel Catalog',
  workbooks_custom_catalog: 'Workbooks Custom Catalog',
  workbooks_interactive_studio: 'Workbooks Interactive Studio',
  workbooks_deployments: 'Workbooks Deployment Wizard',
  workbooks_ai_generator: 'Workbooks AI Generator',
  workbooks_versioning: 'Workbooks Versioning / Rollback',
  workbooks_import_export: 'Workbooks Import / Export',
};

const REPORT_LABELS: { key: keyof ReportRolePolicy; label: string }[] = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'yearly', label: 'Yearly' },
  { key: 'saveMonthlySnapshot', label: 'Save Snapshot' },
  { key: 'downloadHtml', label: 'Download HTML' },
];

type MastersNavLayout = 'horizontal' | 'hamburger';
type MastersSectionId =
  | 'ops'
  | 'watchedDomains'
  | 'discoveryConnections'
  | 'responseConnections'
  | 'guardian'
  | 'neo'
  | 'roleMaster'
  | 'permissionsMaster'
  | 'tabVisibility'
  | 'playbookAccess'
  | 'socAssignment'
  | 'kqlTableAccess'
  | 'reportAccess'
  | 'featureFlags'
  | 'notifications';

const MASTERS_NAV_LAYOUT_KEY = 'kloudsecure:masters:nav-layout';
const ROLE_COLOR_DEFAULTS: Record<string, string> = {
  developer: '#f59e0b',
  admin: '#2563eb',
  software_tester: '#20bfef',
  soc_manager: '#10b981',
  soc_l2: '#8b5cf6',
  soc_l1: '#06b6d4',
  analyst: '#64748b',
  customer_viewer: '#ec4899',
};
const ROLE_COLOR_SWATCHES = ['#f59e0b', '#2563eb', '#20bfef', '#10b981', '#8b5cf6', '#06b6d4', '#ec4899', '#64748b', '#ef4444', '#84cc16', '#14b8a6'];
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const DESTRUCTIVE_CAPABILITY_RE = /(?:^|_)(?:delete|purge|destroy|revoke|remove|drop|wipe|trash)(?:_|$)|(?:^|_)(?:hard_delete|soft_delete|bulk_delete)(?:_|$)/i;
function isSoftwareTesterProtectedCapability(roleId: string, capabilityId: string): boolean {
  return roleId === 'software_tester' && DESTRUCTIVE_CAPABILITY_RE.test(capabilityId);
}

const MASTER_SECTIONS: { id: MastersSectionId; label: string }[] = [
  { id: 'ops', label: 'Operations' },
  { id: 'watchedDomains', label: 'Watched Domains' },
  { id: 'discoveryConnections', label: 'Discovery Master' },
  { id: 'responseConnections', label: 'Response Connections' },
  { id: 'neo', label: 'Neo Master' },
  { id: 'guardian', label: 'Guardian Master' },
  { id: 'roleMaster', label: 'Role Master' },
  { id: 'permissionsMaster', label: 'Permissions Master' },
  { id: 'tabVisibility', label: 'Tab Visibility' },
  { id: 'playbookAccess', label: 'Playbook Access' },
  { id: 'socAssignment', label: 'SOC Assignment' },
  { id: 'kqlTableAccess', label: 'KQL Table Access' },
  { id: 'reportAccess', label: 'Report Access' },
  { id: 'featureFlags', label: 'Feature Flags' },
  { id: 'notifications', label: 'Notifications' },
];

/**
 * One row per nav tab, pointing at the module + capability that gates its
 * visibility. Mirrors `TABS` + `TAB_CAPABILITY_MAP` in App.tsx -- keep both
 * in sync when a tab is added/renamed there. This lets Tab Visibility
 * Master show/toggle every role's "Visible" checkbox across all modules in
 * one grid, instead of clicking through each module in Permissions Master.
 */
const TAB_VISIBILITY_ROWS: { tabId: string; label: string; moduleId: string; capability: string }[] = [
  { tabId: 'mission_control', label: 'Mission Control', moduleId: 'mission_control', capability: 'visible' },
  { tabId: 'ml_insights', label: 'ML Insights Engine', moduleId: 'ml_insights', capability: 'visible' },
  { tabId: 'simulation_lab', label: 'IncidentOps → AI Simulation Lab', moduleId: 'simulation_lab', capability: 'visible' },
  { tabId: 'mssp', label: 'MSSP Overview', moduleId: 'mssp', capability: 'visible' },
  { tabId: 'overview', label: 'Overview', moduleId: 'overview', capability: 'visible' },
  { tabId: 'incidents', label: 'Incidents', moduleId: 'incidents', capability: 'visible' },
  { tabId: 'hunts', label: 'HuntOps', moduleId: 'hunts', capability: 'visible' },
  { tabId: 'workbooks', label: 'Workbooks', moduleId: 'workbooks', capability: 'visible' },
  { tabId: 'notebook_ops', label: 'NotebookOps', moduleId: 'notebooks', capability: 'visible' },
  { tabId: 'responseops', label: 'ResponseOps Studio', moduleId: 'responseops', capability: 'visible' },
  { tabId: 'risk_command_center', label: 'Risk Command Center', moduleId: 'risk_command_center', capability: 'visible' },
  { tabId: 'advanced_hunting', label: 'Advanced Hunting', moduleId: 'advanced_hunting', capability: 'visible' },
  { tabId: 'phishing', label: 'Phishing', moduleId: 'phishing', capability: 'visible' },
  { tabId: 'darkweb', label: 'External Attack Surface', moduleId: 'darkweb', capability: 'visible' },
  { tabId: 'spiderfoot', label: 'OSINT Reconnaissance', moduleId: 'spiderfoot', capability: 'visible' },
  { tabId: 'dnstwist', label: 'Lookalike Domain Scanner', moduleId: 'dnstwist', capability: 'visible' },
  { tabId: 'coverage', label: 'MITRE Coverage', moduleId: 'coverage', capability: 'visible' },
  { tabId: 'oob_coverage_assessment', label: 'OOB Coverage Assessment', moduleId: 'coverage', capability: 'view_oob_assessment' },
  { tabId: 'vulnerability_mgmt', label: 'Vulnerability Management', moduleId: 'vulnerability_mgmt', capability: 'visible' },
  { tabId: 'entity_360', label: 'Entity 360', moduleId: 'entity_360', capability: 'visible' },
  { tabId: 'soc_digital_twin', label: 'SOC Digital Twin', moduleId: 'soc_digital_twin', capability: 'visible' },
  { tabId: 'soc_debt', label: 'SOC Optimization', moduleId: 'soc_debt', capability: 'visible' },
  { tabId: 'report_builder', label: 'Report Builder', moduleId: 'report_builder', capability: 'visible' },
  { tabId: 'connector_health', label: 'Data Connector Health', moduleId: 'connector_health', capability: 'visible' },
  { tabId: 'detection_quality', label: 'Detection Quality', moduleId: 'detection_quality', capability: 'visible' },
  { tabId: 'customer_exec', label: 'Executive Portal', moduleId: 'customer_exec', capability: 'visible' },
  { tabId: 'datalake', label: 'AI Assistant', moduleId: 'ai_assistant', capability: 'visible' },
  { tabId: 'ioc', label: 'Threat Intelligence', moduleId: 'threat_intel', capability: 'visible' },
  { tabId: 'security_copilot', label: 'Security Copilot', moduleId: 'security_copilot', capability: 'visible' },
  { tabId: 'reports', label: 'Reports', moduleId: 'reports', capability: 'visible' },
  { tabId: 'log_master', label: 'Audit Logs', moduleId: 'log_master', capability: 'visible' },
  { tabId: 'masters', label: 'Administration', moduleId: 'masters', capability: 'visible' },
  { tabId: 'logs', label: 'Operational Logs', moduleId: 'logs', capability: 'visible' },
];

function readMastersNavLayout(): MastersNavLayout {
  if (typeof window === 'undefined') return 'horizontal';
  return window.localStorage.getItem(MASTERS_NAV_LAYOUT_KEY) === 'hamburger' ? 'hamburger' : 'horizontal';
}

function asRoleTemplates(value: unknown): RoleTemplate[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        id: String(row.id ?? '').trim(),
        label: String(row.label ?? row.id ?? '').trim(),
        description: String(row.description ?? '').trim(),
        color: HEX_COLOR_RE.test(String(row.color ?? ''))
          ? String(row.color)
          : ROLE_COLOR_DEFAULTS[String(row.id ?? '').trim()] ?? '#64748b',
      };
    })
    .filter((role) => role.id);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((v) => String(v)).filter(Boolean)
    : [];
}

const KQL_TABLE_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{0,127}$/;

function normaliseKqlTableCatalog(value: unknown): string[] {
  const raw = asRecord(value);
  return Array.from(new Set(asStringArray(raw.customTables).filter((table) => KQL_TABLE_NAME_RE.test(table))))
    .sort((a, b) => a.localeCompare(b));
}

function normaliseKqlPolicies(value: unknown, roles: RoleTemplate[]): Record<string, KqlRolePolicy> {
  const raw = asRecord(value);
  const output: Record<string, KqlRolePolicy> = {};

  for (const role of roles) {
    const policy = asRecord(raw[role.id]);
    const allowedRaw = policy.allowedTables;

    output[role.id] = {
      mode: String(policy.mode ?? (allowedRaw === '*' ? 'allow_all_catalog' : 'selected')),
      allowedTables: allowedRaw === '*' ? '*' : asStringArray(allowedRaw),
      deniedTables: asStringArray(policy.deniedTables),
    };
  }

  return output;
}

function normaliseReportAccess(value: unknown, roles: RoleTemplate[]): Record<string, ReportRolePolicy> {
  const raw = asRecord(value);
  const output: Record<string, ReportRolePolicy> = {};

  for (const role of roles) {
    const policy = asRecord(raw[role.id]);

    output[role.id] = {
      daily: Boolean(policy.daily),
      weekly: Boolean(policy.weekly),
      monthly: Boolean(policy.monthly),
      quarterly: Boolean(policy.quarterly),
      yearly: Boolean(policy.yearly),
      saveMonthlySnapshot: Boolean(policy.saveMonthlySnapshot),
      downloadHtml: Boolean(policy.downloadHtml),
    };
  }

  return output;
}

function normaliseFeatureFlags(value: unknown): Record<string, boolean> {
  const raw = asRecord(value);
  const output: Record<string, boolean> = {};

  const keys = new Set([...Object.keys(FEATURE_LABELS), ...Object.keys(raw)]);

  for (const key of keys) {
    output[key] = Boolean(raw[key]);
  }

  return output;
}

function normaliseCapabilityPermissions(
  value: unknown,
  modules: CapabilityModuleDef[],
  roles: RoleTemplate[],
): CapabilityPermissions {
  const raw = asRecord(value);
  const output: CapabilityPermissions = {};

  for (const module of modules) {
    const roleMap = asRecord(raw[module.id]);
    const validCaps = new Set(module.capabilities.map((capability) => capability.id));
    output[module.id] = {};

    for (const role of roles) {
      output[module.id][role.id] = asStringArray(roleMap[role.id])
        .filter((capability) => validCaps.has(capability));
    }
  }

  return output;
}


const WATCHED_DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/;

function normalizeWatchedDomainInput(value: string): string | null {
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./, '');
  if (!cleaned || cleaned.length > 253 || !WATCHED_DOMAIN_RE.test(cleaned)) return null;
  return cleaned;
}

function parseWatchedDomainDraft(value: string): { domains: string[]; invalid: string[] } {
  const rawItems = value
    .split(/[\n,;\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);

  const invalid: string[] = [];
  const domains = Array.from(
    new Set(
      rawItems
        .map((item) => {
          const cleaned = normalizeWatchedDomainInput(item);
          if (!cleaned) invalid.push(item);
          return cleaned;
        })
        .filter((item): item is string => Boolean(item)),
    ),
  ).slice(0, 50);

  return { domains, invalid };
}

function playbookAccessKey(row: Pick<PlaybookAccessItem, 'customerId' | 'resourceId'>): string {
  return `${row.customerId}|||${row.resourceId}`;
}

function parseEmailLines(value: string): string[] {
  return Array.from(new Set(
    value
      .split(/[\n,;\s]+/)
      .map((item) => item.trim().toLowerCase())
      .filter((item) => item && item.includes('@')),
  )).slice(0, 200);
}

function accessSummary(row: PlaybookAccessItem, draft?: PlaybookAccessDraft): string {
  const roles = draft?.allowedRoles ?? row.allowedRoles ?? [];
  const users = parseEmailLines(draft?.allowedUsersText ?? (row.allowedUsers ?? []).join('\n'));
  if (draft?.enabled === false) return 'Disabled';
  return `${roles.length} role${roles.length === 1 ? '' : 's'}${users.length ? ` + ${users.length} user override${users.length === 1 ? '' : 's'}` : ''}`;
}


function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-6 w-11 items-center rounded-full transition',
        checked ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-700',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
      title={label}
    >
      <span
        className={[
          'inline-block h-5 w-5 transform rounded-full bg-white transition',
          checked ? 'translate-x-5' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  );
}

function MastersCard({
  id,
  title,
  description,
  children,
  actions,
}: {
  id?: string;
  title: string;
  description?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 bg-white dark:bg-card rounded-xl shadow p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-bold text-foreground">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
        {actions && <div className="flex gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

function MastersSubNav({
  sections,
  activeSection,
  layout,
  collapsed,
  onLayoutChange,
  onToggleCollapsed,
  onSelect,
}: {
  sections: { id: MastersSectionId; label: string }[];
  activeSection: MastersSectionId;
  layout: MastersNavLayout;
  collapsed: boolean;
  onLayoutChange: (layout: MastersNavLayout) => void;
  onToggleCollapsed: () => void;
  onSelect: (section: MastersSectionId) => void;
}) {
  const activeLabel = sections.find((section) => section.id === activeSection)?.label ?? 'Masters';

  return (
    <div className="rounded-xl border border-border bg-white dark:bg-card shadow p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold text-foreground">Masters Navigation</p>
          <p className="text-[11px] text-muted-foreground">Current section: {activeLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onLayoutChange('horizontal')}
            className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${layout === 'horizontal' ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300' : 'border-border hover:bg-muted'}`}
          >
            Horizontal
          </button>
          <button
            type="button"
            onClick={() => onLayoutChange('hamburger')}
            className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${layout === 'hamburger' ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300' : 'border-border hover:bg-muted'}`}
          >
            Hamburger
          </button>
          {layout === 'hamburger' && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-muted"
            >
              {collapsed ? 'Open menu' : 'Collapse menu'}
            </button>
          )}
        </div>
      </div>

      {layout === 'horizontal' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => onSelect(section.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${activeSection === section.id ? 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-300' : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              {section.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-3 rounded-lg border border-border bg-muted/20">
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-bold text-foreground"
          >
            <span>☰ Masters Menu</span>
            <span className="text-muted-foreground">{collapsed ? 'Show' : 'Hide'}</span>
          </button>
          {!collapsed && (
            <div className="border-t border-border p-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-4">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onSelect(section.id)}
                  className={`rounded-lg px-3 py-2 text-left text-xs font-semibold ${activeSection === section.id ? 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-300' : 'hover:bg-background text-muted-foreground hover:text-foreground'}`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SaveButton({
  saving,
  onClick,
}: {
  saving: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
      onClick={onClick}
      disabled={saving}
    >
      {saving ? 'Saving...' : 'Save'}
    </button>
  );
}

function ResetButton({
  saving,
  onClick,
}: {
  saving: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-muted disabled:opacity-60"
      onClick={onClick}
      disabled={saving}
    >
      Reset
    </button>
  );
}

export function MastersTab() {
  const [data, setData] = useState<MastersPoliciesResponse | null>(null);
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [kqlPolicies, setKqlPolicies] = useState<Record<string, KqlRolePolicy>>({});
  const [customKqlTables, setCustomKqlTables] = useState<string[]>([]);
  const [customKqlTableDraft, setCustomKqlTableDraft] = useState('');
  const [reportAccess, setReportAccess] = useState<Record<string, ReportRolePolicy>>({});
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [capabilityModules, setCapabilityModules] = useState<CapabilityModuleDef[]>([]);
  const [capabilityPermissions, setCapabilityPermissions] = useState<CapabilityPermissions>({});
  const [capabilityUpdatedAt, setCapabilityUpdatedAt] = useState<string | null>(null);
  const [watchedCustomers, setWatchedCustomers] = useState<WatchedDomainCustomer[]>([]);
  const [watchedDomainDrafts, setWatchedDomainDrafts] = useState<Record<string, string>>({});
  const [watchedDomainFilter, setWatchedDomainFilter] = useState('');
  const [watchedDomainCleanupRemovedFindings, setWatchedDomainCleanupRemovedFindings] = useState(true);
  const [watchedDomainSavingCustomerId, setWatchedDomainSavingCustomerId] = useState<string | null>(null);

  const [playbookAccessRows, setPlaybookAccessRows] = useState<PlaybookAccessItem[]>([]);
  const [playbookAccessDrafts, setPlaybookAccessDrafts] = useState<Record<string, PlaybookAccessDraft>>({});
  const [playbookAccessFilter, setPlaybookAccessFilter] = useState('');
  const [playbookAccessCustomerFilter, setPlaybookAccessCustomerFilter] = useState('all');
  const [playbookAccessSaving, setPlaybookAccessSaving] = useState(false);

  const [socAssignmentRows, setSocAssignmentRows] = useState<SocAssignmentItem[]>([]);
  const [socAssignmentCustomers, setSocAssignmentCustomers] = useState<SocAssignmentResponse['customers']>([]);
  const [socAssignmentDrafts, setSocAssignmentDrafts] = useState<Record<string, SocAssignmentDraft>>({});
  const [socAssignmentLighthousePrincipals, setSocAssignmentLighthousePrincipals] = useState<SocAssignmentLighthousePrincipalItem[]>([]);
  const [socAssignmentLighthouseDrafts, setSocAssignmentLighthouseDrafts] = useState<Record<string, SocAssignmentLighthousePrincipalDraft>>({});
  const [socAssignmentNewPrincipal, setSocAssignmentNewPrincipal] = useState({ principalId: '', principalType: 'group', displayName: '', notes: '' });
  const [socAssignmentLighthouseScopes, setSocAssignmentLighthouseScopes] = useState<SocAssignmentLighthouseScopeItem[]>([]);
  const [socAssignmentLighthouseScopeDrafts, setSocAssignmentLighthouseScopeDrafts] = useState<Record<string, SocAssignmentLighthouseScopeDraft>>({});
  const [socAssignmentNewScope, setSocAssignmentNewScope] = useState({ scopeType: 'resourceGroup', subscriptionId: '', resourceGroup: '', displayName: '', notes: '' });
  const [socAssignmentMsspTenantId, setSocAssignmentMsspTenantId] = useState<string | null>(null);
  const [socAssignmentMsspClientId, setSocAssignmentMsspClientId] = useState<string | null>(null);
  const [socAssignmentFilter, setSocAssignmentFilter] = useState('');
  const [socAssignmentCustomerFilter, setSocAssignmentCustomerFilter] = useState('all');
  const [socAssignmentSaving, setSocAssignmentSaving] = useState(false);
  const [socAssignmentVerifyingCustomerId, setSocAssignmentVerifyingCustomerId] = useState<string | null>(null);
  const [socAssignmentLighthouseResult, setSocAssignmentLighthouseResult] = useState<SocAssignmentLighthouseResponse | null>(null);

  const [selectedCapabilityModule, setSelectedCapabilityModule] = useState<string>('');
  const [capabilitySearch, setCapabilitySearch] = useState('');

  const [selectedKqlRole, setSelectedKqlRole] = useState<string>('');
  const [tableSearch, setTableSearch] = useState('');
  const [savingKey, setSavingKey] = useState<PolicyKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [mastersNavLayout, setMastersNavLayoutState] = useState<MastersNavLayout>(() => readMastersNavLayout());
  const [mastersNavCollapsed, setMastersNavCollapsed] = useState<boolean>(() => readMastersNavLayout() === 'hamburger');
  const [activeMastersSection, setActiveMastersSection] = useState<MastersSectionId>('ops');

  const baseKqlTables = data?.catalog?.kqlTables ?? [];
  const kqlTables = useMemo(() => (
    Array.from(new Set([...baseKqlTables, ...customKqlTables])).sort((a, b) => a.localeCompare(b))
  ), [baseKqlTables, customKqlTables]);

  const filteredKqlTables = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return kqlTables;
    return kqlTables.filter((table) => table.toLowerCase().includes(q));
  }, [kqlTables, tableSearch]);

  const filteredCapabilityModules = useMemo(() => {
    const q = capabilitySearch.trim().toLowerCase();
    if (!q) return capabilityModules;
    return capabilityModules.filter((module) =>
      module.label.toLowerCase().includes(q) ||
      module.id.toLowerCase().includes(q) ||
      module.description.toLowerCase().includes(q),
    );
  }, [capabilityModules, capabilitySearch]);

  const filteredWatchedCustomers = useMemo(() => {
    const q = watchedDomainFilter.trim().toLowerCase();
    if (!q) return watchedCustomers;
    return watchedCustomers.filter((customer) =>
      customer.name.toLowerCase().includes(q) ||
      customer.id.toLowerCase().includes(q) ||
      customer.tenantId.toLowerCase().includes(q) ||
      (customer.workspaceName ?? '').toLowerCase().includes(q) ||
      customer.monitoredDomains.some((domain) => domain.toLowerCase().includes(q)),
    );
  }, [watchedCustomers, watchedDomainFilter]);

  const filteredPlaybookAccessRows = useMemo(() => {
    const q = playbookAccessFilter.trim().toLowerCase();
    return playbookAccessRows.filter((row) => {
      if (playbookAccessCustomerFilter !== 'all' && row.customerId !== playbookAccessCustomerFilter) return false;
      if (!q) return true;
      return [
        row.customerName,
        row.customerId,
        row.subscriptionId,
        row.resourceGroup,
        row.workflowName,
        row.resourceId,
        row.state,
        row.triggerType,
        row.description,
        ...(row.connectors ?? []),
      ].some((value) => String(value ?? '').toLowerCase().includes(q));
    });
  }, [playbookAccessRows, playbookAccessFilter, playbookAccessCustomerFilter]);

  function socAssignmentKey(row: Pick<SocAssignmentItem, 'customerId' | 'userEmail'>): string {
    return `${row.customerId}|${row.userEmail.toLowerCase()}`;
  }

  function socAssignmentLighthousePrincipalKey(row: Pick<SocAssignmentLighthousePrincipalItem, 'customerId' | 'principalId'>): string {
    return `${row.customerId}|${row.principalId.toLowerCase()}`;
  }

  function socAssignmentLighthouseScopeKey(row: Pick<SocAssignmentLighthouseScopeItem, 'customerId' | 'subscriptionId' | 'resourceGroup'>): string {
    return `${row.customerId}|${String(row.subscriptionId ?? '').toLowerCase()}|${String(row.resourceGroup ?? '').toLowerCase()}`;
  }

  const filteredSocAssignmentRows = useMemo(() => {
    const q = socAssignmentFilter.trim().toLowerCase();
    return socAssignmentRows.filter((row) => {
      if (socAssignmentCustomerFilter !== 'all' && row.customerId !== socAssignmentCustomerFilter) return false;
      if (!q) return true;
      return [
        row.displayName,
        row.userEmail,
        row.role,
        row.customerId,
        row.source,
        row.notes,
        row.reason,
        ...(row.severityScopes ?? []),
      ].some((value) => String(value ?? '').toLowerCase().includes(q));
    });
  }, [socAssignmentRows, socAssignmentFilter, socAssignmentCustomerFilter]);

  const filteredSocAssignmentLighthousePrincipals = useMemo(() => {
    const selected = socAssignmentCustomerFilter;
    return socAssignmentLighthousePrincipals
      .filter((row) => selected === 'all' || row.customerId === selected)
      .sort((a, b) => (a.customerId.localeCompare(b.customerId) || String(a.displayName || a.principalId).localeCompare(String(b.displayName || b.principalId))));
  }, [socAssignmentLighthousePrincipals, socAssignmentCustomerFilter]);

  const filteredSocAssignmentLighthouseScopes = useMemo(() => {
    const selected = socAssignmentCustomerFilter;
    return socAssignmentLighthouseScopes
      .filter((row) => selected === 'all' || row.customerId === selected)
      .sort((a, b) => (a.customerId.localeCompare(b.customerId) || String(a.resourceGroup || '').localeCompare(String(b.resourceGroup || '')) || a.subscriptionId.localeCompare(b.subscriptionId)));
  }, [socAssignmentLighthouseScopes, socAssignmentCustomerFilter]);

  const selectedSocAssignmentCustomer = useMemo(() => (
    socAssignmentCustomers.find((customer) => customer.id === socAssignmentCustomerFilter) ?? null
  ), [socAssignmentCustomers, socAssignmentCustomerFilter]);

  const selectedSocAssignmentIsMsspTenant = useMemo(() => {
    const msspTenant = String(socAssignmentMsspTenantId ?? '').trim().toLowerCase();
    const selectedTenant = String(selectedSocAssignmentCustomer?.tenantId ?? '').trim().toLowerCase();
    return Boolean(msspTenant && selectedTenant && msspTenant === selectedTenant);
  }, [socAssignmentMsspTenantId, selectedSocAssignmentCustomer?.tenantId]);

  const selectedCapability =
    capabilityModules.find((module) => module.id === selectedCapabilityModule) ??
    filteredCapabilityModules[0] ??
    capabilityModules[0];

  const selectedRole = roleTemplates.find((role) => role.id === selectedKqlRole);
  const selectedPolicy = selectedKqlRole ? kqlPolicies[selectedKqlRole] : undefined;
  const selectedAllowed =
    selectedPolicy?.allowedTables === '*'
      ? new Set<string>(kqlTables)
      : new Set<string>(asStringArray(selectedPolicy?.allowedTables));
  const selectedDenied = new Set<string>(asStringArray(selectedPolicy?.deniedTables));
  const selectedMode = String(selectedPolicy?.mode ?? 'selected');

  async function loadPolicies() {
    setLoading(true);
    setError(null);

    try {
      const [response, capabilityResponse, watchedResponse, playbookAccessResponse, socAssignmentResponse] = await Promise.all([
        apiFetch<MastersPoliciesResponse>('/masters/policies'),
        apiFetch<CapabilityPermissionsResponse>('/masters/permissions'),
        apiFetch<WatchedDomainsResponse>('/masters/watched-domains'),
        apiFetch<PlaybookAccessResponse>('/masters/playbook-access'),
        apiFetch<SocAssignmentResponse>('/masters/soc-assignment'),
      ]);
      const roles = asRoleTemplates(response.policies.roleTemplates);
      const baseRoles = roles.length > 0 ? roles : [
        { id: 'developer', label: 'Developer', description: 'Platform developer.', color: ROLE_COLOR_DEFAULTS.developer },
        { id: 'admin', label: 'Administrator', description: 'Platform administrator.', color: ROLE_COLOR_DEFAULTS.admin },
      ];
      const finalRoles = baseRoles.some((role) => role.id === 'software_tester')
        ? baseRoles
        : [
            ...baseRoles,
            { id: 'software_tester', label: 'Software Tester', description: 'Full validation access with delete/destructive operations blocked by policy.', color: ROLE_COLOR_DEFAULTS.software_tester },
          ];

      const modules = capabilityResponse.catalog.modules ?? response.catalog.capabilityModules ?? [];

      setData(response);
      setRoleTemplates(finalRoles);
      setKqlPolicies(normaliseKqlPolicies(response.policies.kqlTablePolicies, finalRoles));
      setCustomKqlTables(normaliseKqlTableCatalog(response.policies.kqlTableCatalog));
      setReportAccess(normaliseReportAccess(response.policies.reportAccess, finalRoles));
      setFeatureFlags(normaliseFeatureFlags(response.policies.featureFlags));
      setCapabilityModules(modules);
      setCapabilityPermissions(normaliseCapabilityPermissions(capabilityResponse.permissions, modules, finalRoles));
      setCapabilityUpdatedAt(capabilityResponse.updatedAt ?? null);
      setWatchedCustomers(watchedResponse.customers ?? []);
      setWatchedDomainDrafts(Object.fromEntries((watchedResponse.customers ?? []).map((customer) => [
        customer.id,
        (customer.monitoredDomains ?? []).join('\n'),
      ])));


      const playbookRows = playbookAccessResponse.playbooks ?? [];
      setPlaybookAccessRows(playbookRows);
      setPlaybookAccessDrafts(Object.fromEntries(playbookRows.map((row) => [
        playbookAccessKey(row),
        {
          enabled: row.enabled !== false,
          allowedRoles: row.allowedRoles?.length ? row.allowedRoles : ['developer', 'admin'],
          allowedUsersText: (row.allowedUsers ?? []).join('\n'),
        },
      ])));

      const assignmentRows = socAssignmentResponse.assignees ?? [];
      setSocAssignmentRows(assignmentRows);
      setSocAssignmentCustomers(socAssignmentResponse.customers ?? []);
      setSocAssignmentMsspTenantId(socAssignmentResponse.msspTenantId ?? null);
      setSocAssignmentMsspClientId(socAssignmentResponse.msspClientId ?? null);
      setSocAssignmentDrafts(Object.fromEntries(assignmentRows.map((row) => [
        socAssignmentKey(row),
        {
          enabled: row.enabled !== false,
          canAssign: row.canAssign !== false,
          autoAssign: row.autoAssign === true,
          allowServicePrincipalFallback: row.allowServicePrincipalFallback === true,
          severityScopes: row.severityScopes?.length ? row.severityScopes : ['Any'],
          lighthouseVerified: row.lighthouseVerified === true,
          notes: row.notes ?? '',
        },
      ])));
      const principalRows = socAssignmentResponse.lighthousePrincipals ?? [];
      setSocAssignmentLighthousePrincipals(principalRows);
      setSocAssignmentLighthouseDrafts(Object.fromEntries(principalRows.map((row) => [
        socAssignmentLighthousePrincipalKey(row),
        {
          enabled: row.enabled !== false,
          principalType: row.principalType || 'group',
          displayName: row.displayName ?? '',
          notes: row.notes ?? '',
        },
      ])));
      const scopeRows = socAssignmentResponse.lighthouseScopes ?? [];
      setSocAssignmentLighthouseScopes(scopeRows);
      setSocAssignmentLighthouseScopeDrafts(Object.fromEntries(scopeRows.map((row) => [
        socAssignmentLighthouseScopeKey(row),
        {
          enabled: row.enabled !== false,
          scopeType: row.scopeType || 'subscription',
          subscriptionId: row.subscriptionId ?? '',
          resourceGroup: row.resourceGroup ?? '',
          displayName: row.displayName ?? '',
          notes: row.notes ?? '',
        },
      ])));

      setSelectedKqlRole((current) => current || finalRoles[0]?.id || '');
      setSelectedCapabilityModule((current) => current || modules[0]?.id || '');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function savePolicy(key: PolicyKey, payload: unknown) {
    setSavingKey(key);
    setError(null);
    setSavedMessage(null);

    try {
      await apiFetch<{ ok: boolean; key: string }>(`/masters/policies/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload }),
      });

      setSavedMessage(`${POLICY_TITLES[key]} saved successfully.`);
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKey(null);
    }
  }

  async function resetPolicy(key: PolicyKey) {
    const confirmed = window.confirm(`Reset ${POLICY_TITLES[key]} to the default policy?`);
    if (!confirmed) return;

    setSavingKey(key);
    setError(null);
    setSavedMessage(null);

    try {
      await apiFetch<{ ok: boolean; key: string; reset: boolean }>(`/masters/policies/${key}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      setSavedMessage(`${POLICY_TITLES[key]} reset to defaults.`);
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKey(null);
    }
  }

  async function saveCapabilityMatrix() {
    setSavingKey('capabilityPermissions');
    setError(null);
    setSavedMessage(null);

    try {
      await apiFetch<{ ok: boolean; key: string; permissions: CapabilityPermissions }>(`/masters/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: capabilityPermissions }),
      });

      setSavedMessage('Permissions Master saved successfully. Runtime enforcement will be wired in later phases.');
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKey(null);
    }
  }

  async function resetCapabilityMatrix() {
    const confirmed = window.confirm('Reset Permissions Master to the default capability matrix?');
    if (!confirmed) return;

    setSavingKey('capabilityPermissions');
    setError(null);
    setSavedMessage(null);

    try {
      await apiFetch<{ ok: boolean; key: string; reset: boolean; permissions: CapabilityPermissions }>(`/masters/permissions/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      setSavedMessage('Permissions Master reset to defaults.');
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKey(null);
    }
  }

  function updateWatchedDomainDraft(customerId: string, value: string) {
    setWatchedDomainDrafts((prev) => ({ ...prev, [customerId]: value }));
  }

  function addWatchedDomainToDraft(customerId: string) {
    const value = window.prompt('Enter domain to monitor, for example acme.com');
    if (!value) return;
    const cleaned = normalizeWatchedDomainInput(value);
    if (!cleaned) {
      setError(`Invalid domain: ${value}`);
      return;
    }
    setWatchedDomainDrafts((prev) => {
      const current = parseWatchedDomainDraft(prev[customerId] ?? '').domains;
      const next = Array.from(new Set([...current, cleaned])).sort();
      return { ...prev, [customerId]: next.join('\n') };
    });
  }

  function removeWatchedDomainFromDraft(customerId: string, domain: string) {
    setWatchedDomainDrafts((prev) => {
      const next = parseWatchedDomainDraft(prev[customerId] ?? '')
        .domains
        .filter((item) => item !== domain);
      return { ...prev, [customerId]: next.join('\n') };
    });
  }

  async function saveWatchedDomains(customer: WatchedDomainCustomer) {
    const parsed = parseWatchedDomainDraft(watchedDomainDrafts[customer.id] ?? '');
    if (parsed.invalid.length > 0) {
      setError(`Invalid watched domain entr${parsed.invalid.length === 1 ? 'y' : 'ies'} for ${customer.name}: ${parsed.invalid.slice(0, 5).join(', ')}`);
      return;
    }

    setWatchedDomainSavingCustomerId(customer.id);
    setError(null);
    setSavedMessage(null);

    try {
      const result = await apiFetch<WatchedDomainsUpdateResponse>(`/masters/watched-domains/${encodeURIComponent(customer.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domains: parsed.domains,
          acknowledgeRemovedFindings: watchedDomainCleanupRemovedFindings,
        }),
      });

      setSavedMessage(
        `Watched domains updated for ${customer.name}. Removed ${result.removedDomains.length} domain${result.removedDomains.length === 1 ? '' : 's'}${result.acknowledgedFindings ? ` and acknowledged ${result.acknowledgedFindings} stale finding${result.acknowledgedFindings === 1 ? '' : 's'}` : ''}.`,
      );
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setWatchedDomainSavingCustomerId(null);
    }
  }

  function updatePlaybookAccessDraft(row: PlaybookAccessItem, patch: Partial<PlaybookAccessDraft>) {
    const key = playbookAccessKey(row);
    setPlaybookAccessDrafts((prev) => {
      const current = prev[key] ?? {
        enabled: row.enabled !== false,
        allowedRoles: row.allowedRoles?.length ? row.allowedRoles : ['developer', 'admin'],
        allowedUsersText: (row.allowedUsers ?? []).join('\n'),
      };
      return { ...prev, [key]: { ...current, ...patch } };
    });
  }

  function togglePlaybookAccessRole(row: PlaybookAccessItem, roleId: string) {
    const key = playbookAccessKey(row);
    const draft = playbookAccessDrafts[key] ?? { enabled: row.enabled !== false, allowedRoles: row.allowedRoles ?? [], allowedUsersText: (row.allowedUsers ?? []).join('\n') };
    const current = new Set(draft.allowedRoles ?? []);
    if (current.has(roleId)) current.delete(roleId);
    else current.add(roleId);
    const ordered = roleTemplates.map((role) => role.id).filter((id) => current.has(id));
    updatePlaybookAccessDraft(row, { allowedRoles: ordered });
  }

  async function savePlaybookAccessMaster() {
    setPlaybookAccessSaving(true);
    setError(null);
    setSavedMessage(null);

    try {
      const policies = playbookAccessRows.map((row) => {
        const draft = playbookAccessDrafts[playbookAccessKey(row)] ?? { enabled: row.enabled !== false, allowedRoles: row.allowedRoles ?? [], allowedUsersText: (row.allowedUsers ?? []).join('\n') };
        return {
          customerId: row.customerId,
          resourceId: row.resourceId,
          workflowName: row.workflowName,
          subscriptionId: row.subscriptionId ?? '',
          resourceGroup: row.resourceGroup ?? '',
          enabled: draft.enabled !== false,
          allowedRoles: draft.allowedRoles ?? [],
          allowedUsers: parseEmailLines(draft.allowedUsersText ?? ''),
        };
      });
      const result = await apiFetch<{ ok: boolean; saved: number; updatedAt: string }>('/masters/playbook-access', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policies }),
      });
      setSavedMessage(`Playbook Access Master saved. ${result.saved} playbook polic${result.saved === 1 ? 'y' : 'ies'} updated.`);
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPlaybookAccessSaving(false);
    }
  }

  function updateSocAssignmentDraft(row: SocAssignmentItem, patch: Partial<SocAssignmentDraft>) {
    const key = socAssignmentKey(row);
    setSocAssignmentDrafts((prev) => {
      const current = prev[key] ?? {
        enabled: row.enabled !== false,
        canAssign: row.canAssign !== false,
        autoAssign: row.autoAssign === true,
        allowServicePrincipalFallback: row.allowServicePrincipalFallback === true,
        severityScopes: row.severityScopes?.length ? row.severityScopes : ['Any'],
        lighthouseVerified: row.lighthouseVerified === true,
        notes: row.notes ?? '',
      };
      return { ...prev, [key]: { ...current, ...patch } };
    });
  }

  function toggleSocAssignmentSeverity(row: SocAssignmentItem, severity: string) {
    const key = socAssignmentKey(row);
    const draft = socAssignmentDrafts[key] ?? { enabled: row.enabled !== false, canAssign: row.canAssign !== false, autoAssign: row.autoAssign === true, allowServicePrincipalFallback: row.allowServicePrincipalFallback === true, severityScopes: row.severityScopes ?? ['Any'], lighthouseVerified: row.lighthouseVerified === true, notes: row.notes ?? '' };
    let current = new Set(draft.severityScopes ?? ['Any']);
    if (severity === 'Any') {
      current = new Set(['Any']);
    } else {
      current.delete('Any');
      if (current.has(severity)) current.delete(severity);
      else current.add(severity);
      if (current.size === 0) current.add('Any');
    }
    const ordered = ASSIGNMENT_SEVERITY_OPTIONS.filter((item) => current.has(item));
    updateSocAssignmentDraft(row, { severityScopes: ordered });
  }

  function updateSocAssignmentLighthouseDraft(row: SocAssignmentLighthousePrincipalItem, patch: Partial<SocAssignmentLighthousePrincipalDraft>) {
    const key = socAssignmentLighthousePrincipalKey(row);
    setSocAssignmentLighthouseDrafts((prev) => {
      const defaults: SocAssignmentLighthousePrincipalDraft = {
        enabled: row.enabled !== false,
        principalType: row.principalType || 'group',
        displayName: row.displayName ?? '',
        notes: row.notes ?? '',
      };
      return {
        ...prev,
        [key]: {
          ...defaults,
          ...(prev[key] ?? {}),
          ...patch,
        },
      };
    });
  }

  function updateSocAssignmentLighthouseScopeDraft(row: SocAssignmentLighthouseScopeItem, patch: Partial<SocAssignmentLighthouseScopeDraft>) {
    const key = socAssignmentLighthouseScopeKey(row);
    setSocAssignmentLighthouseScopeDrafts((prev) => {
      const defaults: SocAssignmentLighthouseScopeDraft = {
        enabled: row.enabled !== false,
        scopeType: row.scopeType || 'subscription',
        subscriptionId: row.subscriptionId ?? selectedSocAssignmentCustomer?.subscription ?? '',
        resourceGroup: row.resourceGroup ?? '',
        displayName: row.displayName ?? '',
        notes: row.notes ?? '',
      };
      return {
        ...prev,
        [key]: {
          ...defaults,
          ...(prev[key] ?? {}),
          ...patch,
        },
      };
    });
  }

  function addSocAssignmentLighthousePrincipal() {
    if (socAssignmentCustomerFilter === 'all') {
      setError('Select a single customer before adding MSSP/Lighthouse principal object IDs.');
      return;
    }
    if (selectedSocAssignmentIsMsspTenant) {
      setError('This is the MSSP home tenant. Lighthouse principal registry is not applicable.');
      return;
    }
    const principalId = socAssignmentNewPrincipal.principalId.trim();
    if (!principalId) {
      setError('Enter the Entra object ID of the Lighthouse user/group/service principal.');
      return;
    }
    const row: SocAssignmentLighthousePrincipalItem = {
      id: `draft-${Date.now()}`,
      customerId: socAssignmentCustomerFilter,
      principalId,
      principalType: socAssignmentNewPrincipal.principalType || 'group',
      displayName: socAssignmentNewPrincipal.displayName.trim() || null,
      enabled: true,
      source: 'manual_mssp_registry',
      notes: socAssignmentNewPrincipal.notes.trim() || null,
    };
    const key = socAssignmentLighthousePrincipalKey(row);
    setSocAssignmentLighthousePrincipals((prev) => {
      const existing = prev.filter((item) => socAssignmentLighthousePrincipalKey(item) !== key);
      return [...existing, row];
    });
    setSocAssignmentLighthouseDrafts((prev) => ({
      ...prev,
      [key]: {
        enabled: true,
        principalType: row.principalType,
        displayName: row.displayName ?? '',
        notes: row.notes ?? '',
      },
    }));
    setSocAssignmentNewPrincipal({ principalId: '', principalType: 'group', displayName: '', notes: '' });
    setSavedMessage('Lighthouse principal added to the draft. Click Save to persist it.');
  }

  function addSocAssignmentLighthouseScope() {
    if (socAssignmentCustomerFilter === 'all') {
      setError('Select a single customer before adding a Lighthouse delegated scope.');
      return;
    }
    if (selectedSocAssignmentIsMsspTenant) {
      setError('This is the MSSP home tenant. Delegated Lighthouse scopes are not applicable.');
      return;
    }
    const subscriptionId = (socAssignmentNewScope.subscriptionId || selectedSocAssignmentCustomer?.subscription || '').trim();
    const scopeType = socAssignmentNewScope.scopeType || 'resourceGroup';
    const resourceGroup = scopeType === 'resourceGroup' ? socAssignmentNewScope.resourceGroup.trim() : '';
    if (!subscriptionId) {
      setError('Enter the customer subscription ID for this delegated scope.');
      return;
    }
    if (scopeType === 'resourceGroup' && !resourceGroup) {
      setError('Enter the delegated resource group name, or switch the scope type to Subscription.');
      return;
    }
    const row: SocAssignmentLighthouseScopeItem = {
      id: `draft-scope-${Date.now()}`,
      customerId: socAssignmentCustomerFilter,
      scopeType,
      subscriptionId,
      resourceGroup: resourceGroup || null,
      displayName: socAssignmentNewScope.displayName.trim() || null,
      enabled: true,
      notes: socAssignmentNewScope.notes.trim() || null,
    };
    const key = socAssignmentLighthouseScopeKey(row);
    setSocAssignmentLighthouseScopes((prev) => {
      const existing = prev.filter((item) => socAssignmentLighthouseScopeKey(item) !== key);
      return [...existing, row];
    });
    setSocAssignmentLighthouseScopeDrafts((prev) => ({
      ...prev,
      [key]: {
        enabled: true,
        scopeType: row.scopeType,
        subscriptionId: row.subscriptionId,
        resourceGroup: row.resourceGroup ?? '',
        displayName: row.displayName ?? '',
        notes: row.notes ?? '',
      },
    }));
    setSocAssignmentNewScope({ scopeType: 'resourceGroup', subscriptionId: selectedSocAssignmentCustomer?.subscription ?? '', resourceGroup: '', displayName: '', notes: '' });
    setSavedMessage('Delegated Lighthouse scope added to the draft. Click Save to persist it.');
  }

  async function deleteSocAssignmentLighthousePrincipal(row: SocAssignmentLighthousePrincipalItem) {
    const label = row.displayName || row.principalId;
    if (!window.confirm(`Delete Lighthouse principal registry entry ${label}? This is a soft delete and will be audited.`)) return;
    const key = socAssignmentLighthousePrincipalKey(row);
    if (!row.id || row.id.startsWith('draft-')) {
      setSocAssignmentLighthousePrincipals((prev) => prev.filter((item) => socAssignmentLighthousePrincipalKey(item) !== key));
      setSocAssignmentLighthouseDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setSocAssignmentSaving(true);
    setError(null);
    try {
      await apiFetch(`/masters/soc-assignment/lighthouse-principals/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
      setSavedMessage('Lighthouse principal registry entry deleted.');
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSocAssignmentSaving(false);
    }
  }

  async function deleteSocAssignmentLighthouseScope(row: SocAssignmentLighthouseScopeItem) {
    const label = row.displayName || row.resourceGroup || row.subscriptionId;
    if (!window.confirm(`Delete Lighthouse delegated scope ${label}? This is a soft delete and will be audited.`)) return;
    const key = socAssignmentLighthouseScopeKey(row);
    if (!row.id || row.id.startsWith('draft-')) {
      setSocAssignmentLighthouseScopes((prev) => prev.filter((item) => socAssignmentLighthouseScopeKey(item) !== key));
      setSocAssignmentLighthouseScopeDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setSocAssignmentSaving(true);
    setError(null);
    try {
      await apiFetch(`/masters/soc-assignment/lighthouse-scopes/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
      setSavedMessage('Lighthouse delegated scope deleted.');
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSocAssignmentSaving(false);
    }
  }

  async function saveSocAssignmentMaster() {
    setSocAssignmentSaving(true);
    setError(null);
    setSavedMessage(null);

    try {
      const policies = socAssignmentRows.map((row) => {
        const draft = socAssignmentDrafts[socAssignmentKey(row)] ?? { enabled: row.enabled !== false, canAssign: row.canAssign !== false, autoAssign: row.autoAssign === true, allowServicePrincipalFallback: row.allowServicePrincipalFallback === true, severityScopes: row.severityScopes ?? ['Any'], lighthouseVerified: row.lighthouseVerified === true, notes: row.notes ?? '' };
        return {
          customerId: row.customerId,
          userEmail: row.userEmail,
          enabled: draft.enabled !== false,
          canAssign: draft.canAssign !== false,
          autoAssign: draft.autoAssign === true,
          allowServicePrincipalFallback: draft.allowServicePrincipalFallback === true,
          severityScopes: draft.severityScopes?.length ? draft.severityScopes : ['Any'],
          lighthouseVerified: draft.lighthouseVerified === true,
          lastVerifiedAt: draft.lighthouseVerified === true ? (row.lastVerifiedAt ?? new Date().toISOString()) : null,
          source: draft.lighthouseVerified ? 'azure_lighthouse' : (row.source === 'azure_lighthouse' ? 'kloudsecure_user' : (row.source || 'kloudsecure_user')),
          notes: draft.notes ?? '',
        };
      });
      const lighthousePrincipals = selectedSocAssignmentIsMsspTenant ? [] : socAssignmentLighthousePrincipals.map((row) => {
        const draft = socAssignmentLighthouseDrafts[socAssignmentLighthousePrincipalKey(row)] ?? {
          enabled: row.enabled !== false,
          principalType: row.principalType || 'group',
          displayName: row.displayName ?? '',
          notes: row.notes ?? '',
        };
        return {
          customerId: row.customerId,
          principalId: row.principalId,
          principalType: draft.principalType || 'group',
          displayName: draft.displayName,
          enabled: draft.enabled !== false,
          source: row.source || 'manual_mssp_registry',
          notes: draft.notes,
        };
      });
      const lighthouseScopes = selectedSocAssignmentIsMsspTenant ? [] : socAssignmentLighthouseScopes.map((row) => {
        const draft = socAssignmentLighthouseScopeDrafts[socAssignmentLighthouseScopeKey(row)] ?? {
          enabled: row.enabled !== false,
          scopeType: row.scopeType || 'subscription',
          subscriptionId: row.subscriptionId ?? '',
          resourceGroup: row.resourceGroup ?? '',
          displayName: row.displayName ?? '',
          notes: row.notes ?? '',
        };
        const scopeType = draft.scopeType || 'subscription';
        return {
          customerId: row.customerId,
          scopeType,
          subscriptionId: draft.subscriptionId || row.subscriptionId,
          resourceGroup: scopeType === 'resourceGroup' ? draft.resourceGroup : '',
          displayName: draft.displayName,
          enabled: draft.enabled !== false,
          notes: draft.notes,
        };
      });
      const result = await apiFetch<{ ok: boolean; saved: number; savedLighthousePrincipals?: number; savedLighthouseScopes?: number; updatedAt?: string }>(`/masters/soc-assignment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policies, lighthousePrincipals, lighthouseScopes }),
      });
      setSavedMessage(`SOC Assignment Master saved. ${result.saved} assignee polic${result.saved === 1 ? 'y' : 'ies'}, ${result.savedLighthousePrincipals ?? 0} Lighthouse principal entr${(result.savedLighthousePrincipals ?? 0) === 1 ? 'y' : 'ies'}, and ${result.savedLighthouseScopes ?? 0} delegated scope entr${(result.savedLighthouseScopes ?? 0) === 1 ? 'y' : 'ies'} updated.`);
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSocAssignmentSaving(false);
    }
  }

  async function refreshSocAssignmentLighthouse() {
    if (socAssignmentCustomerFilter === 'all') {
      setError('Select a single customer before running Azure Lighthouse verification.');
      return;
    }

    if (selectedSocAssignmentIsMsspTenant) {
      setError('This is the MSSP home tenant. Azure Lighthouse verification is not applicable.');
      return;
    }

    const customerName = socAssignmentCustomers.find((customer) => customer.id === socAssignmentCustomerFilter)?.name || socAssignmentCustomerFilter;
    const confirmed = window.confirm(`Refresh Azure Lighthouse verification for ${customerName}? This reads Microsoft.ManagedServices registration assignments at subscription and configured resource-group scopes, then expands managing-tenant Graph groups.`);
    if (!confirmed) return;

    setSocAssignmentVerifyingCustomerId(socAssignmentCustomerFilter);
    setError(null);
    setSavedMessage(null);

    try {
      const result = await apiFetch<SocAssignmentLighthouseResponse>('/masters/soc-assignment/lighthouse-refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: socAssignmentCustomerFilter }),
      });
      setSocAssignmentLighthouseResult(result);
      setSavedMessage(`${result.message} Policies updated: ${result.updatedPolicies}.`);
      await loadPolicies();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSocAssignmentVerifyingCustomerId(null);
    }
  }

  function updateRole(index: number, patch: Partial<RoleTemplate>) {
    setRoleTemplates((prev) =>
      prev.map((role, i) => (i === index ? { ...role, ...patch } : role)),
    );
  }

  function addRole() {
    const id = window.prompt('Enter new role ID, for example soc_l3');
    if (!id) return;

    const cleanId = id.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
    if (!cleanId) return;

    if (roleTemplates.some((role) => role.id === cleanId)) {
      setError(`Role '${cleanId}' already exists.`);
      return;
    }

    const newRole: RoleTemplate = {
      id: cleanId,
      label: cleanId.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
      description: '',
      color: '#64748b',
    };

    setRoleTemplates((prev) => [...prev, newRole]);
    setKqlPolicies((prev) => ({
      ...prev,
      [cleanId]: {
        mode: 'selected',
        allowedTables: [],
        deniedTables: [],
      },
    }));
    setReportAccess((prev) => ({
      ...prev,
      [cleanId]: {
        daily: false,
        weekly: false,
        monthly: false,
        quarterly: false,
        yearly: false,
        saveMonthlySnapshot: false,
        downloadHtml: false,
      },
    }));
    setCapabilityPermissions((prev) => {
      const next: CapabilityPermissions = {};
      for (const module of capabilityModules) {
        next[module.id] = { ...(prev[module.id] ?? {}), [cleanId]: [] };
      }
      return next;
    });
    setSelectedKqlRole(cleanId);
  }

  function removeRole(id: string) {
    if (['developer', 'admin', 'software_tester'].includes(id)) {
      setError('Developer, Administrator and Software Tester roles cannot be removed from the Masters UI.');
      return;
    }

    const confirmed = window.confirm(`Remove role '${id}' from role templates and related policies?`);
    if (!confirmed) return;

    setRoleTemplates((prev) => prev.filter((role) => role.id !== id));

    setKqlPolicies((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    setReportAccess((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    setCapabilityPermissions((prev) => {
      const next: CapabilityPermissions = {};
      for (const [moduleId, roleMap] of Object.entries(prev)) {
        const nextRoleMap = { ...roleMap };
        delete nextRoleMap[id];
        next[moduleId] = nextRoleMap;
      }
      return next;
    });

    if (selectedKqlRole === id) {
      setSelectedKqlRole(roleTemplates.find((role) => role.id !== id)?.id || '');
    }
  }

  function updateKqlPolicy(roleId: string, patch: Partial<KqlRolePolicy>) {
    setKqlPolicies((prev) => ({
      ...prev,
      [roleId]: {
        ...(prev[roleId] ?? { mode: 'selected', allowedTables: [], deniedTables: [] }),
        ...patch,
      },
    }));
  }

  function setRoleMode(roleId: string, mode: 'allow_all_catalog' | 'selected') {
    updateKqlPolicy(roleId, {
      mode,
      allowedTables: mode === 'allow_all_catalog' ? '*' : [],
    });
  }

  function toggleAllowedTable(roleId: string, table: string) {
    const current = kqlPolicies[roleId];
    const allowed = current?.allowedTables === '*'
      ? new Set(kqlTables)
      : new Set(asStringArray(current?.allowedTables));

    if (allowed.has(table)) {
      allowed.delete(table);
    } else {
      allowed.add(table);
    }

    updateKqlPolicy(roleId, {
      mode: 'selected',
      allowedTables: Array.from(allowed).sort(),
    });
  }

  function addDeniedTable(roleId: string, table: string) {
    const current = kqlPolicies[roleId];
    const denied = new Set(asStringArray(current?.deniedTables));
    denied.add(table);

    updateKqlPolicy(roleId, {
      deniedTables: Array.from(denied).sort(),
    });
  }

  function removeDeniedTable(roleId: string, table: string) {
    const current = kqlPolicies[roleId];
    const denied = new Set(asStringArray(current?.deniedTables));
    denied.delete(table);

    updateKqlPolicy(roleId, {
      deniedTables: Array.from(denied).sort(),
    });
  }


  function addCustomKqlTable() {
    const table = customKqlTableDraft.trim();
    if (!table) return;
    if (!KQL_TABLE_NAME_RE.test(table)) {
      setError('Invalid table name. Use letters, numbers and underscores only, starting with a letter. Example: Usage or CustomTable_CL');
      return;
    }
    if (kqlTables.includes(table) || customKqlTables.includes(table)) {
      setError(`KQL table '${table}' already exists in the catalog.`);
      return;
    }
    setCustomKqlTables((prev) => Array.from(new Set([...prev, table])).sort((a, b) => a.localeCompare(b)));
    setCustomKqlTableDraft('');
    setSavedMessage(`Added '${table}' to the catalog draft. Click Save Catalog to persist it, then allow/deny it per role below.`);
  }

  function removeCustomKqlTable(table: string) {
    const confirmed = window.confirm(`Remove '${table}' from the global KQL catalog? Existing role policy references will no longer appear in the picker.`);
    if (!confirmed) return;
    setCustomKqlTables((prev) => prev.filter((item) => item !== table));
  }

  function toggleReportAccess(roleId: string, key: keyof ReportRolePolicy) {
    setReportAccess((prev) => ({
      ...prev,
      [roleId]: {
        ...(prev[roleId] ?? {}),
        [key]: !Boolean(prev[roleId]?.[key]),
      },
    }));
  }

  function toggleFeatureFlag(key: string) {
    setFeatureFlags((prev) => ({
      ...prev,
      [key]: !Boolean(prev[key]),
    }));
  }

  function setMastersNavLayout(layout: MastersNavLayout) {
    setMastersNavLayoutState(layout);
    setMastersNavCollapsed(layout === 'hamburger');
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MASTERS_NAV_LAYOUT_KEY, layout);
    }
  }

  function jumpToMastersSection(section: MastersSectionId) {
    setActiveMastersSection(section);
    const element = typeof document !== 'undefined' ? document.getElementById(`masters-section-${section}`) : null;
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function toggleCapability(moduleId: string, roleId: string, capabilityId: string) {
    if (isSoftwareTesterProtectedCapability(roleId, capabilityId)) {
      setSavedMessage('Software Tester cannot be granted delete/destructive capabilities. Backend deny policy also enforces this.');
      return;
    }
    setCapabilityPermissions((prev) => {
      const current = new Set(prev[moduleId]?.[roleId] ?? []);
      if (current.has(capabilityId)) {
        current.delete(capabilityId);
      } else {
        current.add(capabilityId);
      }

      const moduleDef = capabilityModules.find((module) => module.id === moduleId);
      const ordered = (moduleDef?.capabilities ?? [])
        .map((capability) => capability.id)
        .filter((capability) => current.has(capability));

      return {
        ...prev,
        [moduleId]: {
          ...(prev[moduleId] ?? {}),
          [roleId]: ordered,
        },
      };
    });
  }

  function setAllCapabilities(moduleId: string, roleId: string, checked: boolean) {
    const moduleDef = capabilityModules.find((module) => module.id === moduleId);
    const capabilities = checked
      ? (moduleDef?.capabilities ?? [])
          .filter((capability) => !isSoftwareTesterProtectedCapability(roleId, capability.id))
          .map((capability) => capability.id)
      : [];

    setCapabilityPermissions((prev) => ({
      ...prev,
      [moduleId]: {
        ...(prev[moduleId] ?? {}),
        [roleId]: capabilities,
      },
    }));
  }

  useEffect(() => {
    void loadPolicies();
  }, []);

  if (loading) {
    return (
      <div className="bg-white dark:bg-card rounded-xl shadow p-6">
        <p className="text-sm text-muted-foreground">Loading Masters policies...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 rounded-xl p-4 text-sm">
        <strong>Masters module unavailable:</strong> {error}
      </div>
    );
  }

  return (
    <div className="slide-in space-y-4">
      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <h2 className="text-lg font-bold text-foreground">Masters</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Developer-only governance console for role templates, KQL table access, report permissions, and platform feature flags.
        </p>

        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900">
            Policies saved to PostgreSQL
          </span>
          <span className="px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900">
            Changes audited in audit_events
          </span>
        </div>
      </div>

      <MastersSubNav
        sections={MASTER_SECTIONS}
        activeSection={activeMastersSection}
        layout={mastersNavLayout}
        collapsed={mastersNavCollapsed}
        onLayoutChange={setMastersNavLayout}
        onToggleCollapsed={() => setMastersNavCollapsed((prev) => !prev)}
        onSelect={jumpToMastersSection}
      />

      <div id="masters-section-ops" className="scroll-mt-24">
        <MastersOpsPanel />
      </div>

      <div id="masters-section-discoveryConnections" className="scroll-mt-24">
        {featureFlags['discovery_broker_native'] === false ? (
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">Native Discovery Broker is disabled by the <span className="font-mono">discovery_broker_native</span> Feature Flag.</div>
        ) : (
          <DiscoveryConnectionMaster />
        )}
      </div>

      <div id="masters-section-responseConnections" className="scroll-mt-24">
        {featureFlags['response_broker_native'] === false ? (
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">Native Response Broker is disabled by the <span className="font-mono">response_broker_native</span> Feature Flag.</div>
        ) : (
          <ResponseConnectionMaster />
        )}
      </div>

      <div id="masters-section-neo" className="scroll-mt-24">
        {featureFlags['neo_control_plane_stage5b1'] === false ? (
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">Neo Control Plane is disabled by the <span className="font-mono">neo_control_plane_stage5b1</span> Feature Flag.</div>
        ) : (
          <NeoMaster />
        )}
      </div>

      <div id="masters-section-guardian" className="scroll-mt-24">
        {featureFlags['guardian_soc_stage5'] === false ? (
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">Guardian SOC is disabled by the <span className="font-mono">guardian_soc_stage5</span> Feature Flag.</div>
        ) : (
          <GuardianMaster />
        )}
      </div>

      {savedMessage && (
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 p-4 text-sm">
          {savedMessage}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 p-4 text-sm">
          {error}
        </div>
      )}

      <MastersCard
        id="masters-section-watchedDomains"
        title="External Exposure Watched Domains Master"
        description="Central list of customer domains monitored by Neo External Exposure Center sources such as dnstwist lookalikes, crt.sh, GitHub, URLScan, Shodan, HIBP and Hudson Rock. Remove sample/demo domains here."
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <input
            className="w-full lg:max-w-md border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
            placeholder="Search customers, workspaces or domains..."
            value={watchedDomainFilter}
            onChange={(e) => setWatchedDomainFilter(e.target.value)}
          />
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={watchedDomainCleanupRemovedFindings}
              onChange={(e) => setWatchedDomainCleanupRemovedFindings(e.target.checked)}
            />
            Acknowledge existing unacked findings for domains removed from the watch list
          </label>
        </div>

        <div className="space-y-4">
          {filteredWatchedCustomers.length === 0 ? (
            <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">
              No customers match the current filter.
            </div>
          ) : filteredWatchedCustomers.map((customer) => {
            const parsed = parseWatchedDomainDraft(watchedDomainDrafts[customer.id] ?? '');
            const original = customer.monitoredDomains ?? [];
            const originalSet = new Set(original);
            const draftSet = new Set(parsed.domains);
            const removed = original.filter((domain) => !draftSet.has(domain));
            const added = parsed.domains.filter((domain) => !originalSet.has(domain));
            const changed = removed.length > 0 || added.length > 0 || parsed.invalid.length > 0;
            const saving = watchedDomainSavingCustomerId === customer.id;

            return (
              <div key={customer.id} className="border border-border rounded-xl p-4">
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-bold text-foreground">{customer.name}</h4>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {parsed.domains.length} watched
                      </span>
                      {customer.neoEnabled ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900">
                          Neo enabled
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-900">
                          Neo disabled
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Workspace: {customer.workspaceName || '—'} · Tenant: <span className="font-mono">{customer.tenantId}</span>
                    </div>
                    {customer.neoLastRunAt ? (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Last sweep {fmtTs(customer.neoLastRunAt)} · Open findings: {customer.neoLastRunSummary?.totalUnacked ?? 0}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => addWatchedDomainToDraft(customer.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-muted"
                    >
                      Add domain
                    </button>
                    <button
                      type="button"
                      onClick={() => void saveWatchedDomains(customer)}
                      disabled={saving || !changed}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Save domains'}
                    </button>
                  </div>
                </div>

                <textarea
                  className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm min-h-[110px] font-mono"
                  value={watchedDomainDrafts[customer.id] ?? ''}
                  onChange={(e) => updateWatchedDomainDraft(customer.id, e.target.value)}
                  placeholder="one-domain-per-line.com"
                />

                <div className="mt-3 flex flex-wrap gap-2">
                  {parsed.domains.length === 0 ? (
                    <span className="text-xs text-muted-foreground">No watched domains configured.</span>
                  ) : parsed.domains.map((domain) => (
                    <button
                      key={domain}
                      type="button"
                      onClick={() => removeWatchedDomainFromDraft(customer.id, domain)}
                      className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-border hover:border-red-300 hover:text-red-600"
                      title="Remove from draft"
                    >
                      {domain} ×
                    </button>
                  ))}
                </div>

                {parsed.invalid.length > 0 ? (
                  <div className="mt-3 text-xs text-red-600 dark:text-red-300">
                    Invalid entries: {parsed.invalid.slice(0, 8).join(', ')}
                  </div>
                ) : null}
                {removed.length > 0 ? (
                  <div className="mt-3 text-xs text-amber-700 dark:text-amber-300">
                    Will remove: {removed.join(', ')}
                    {watchedDomainCleanupRemovedFindings ? ' and acknowledge existing unacked findings for those domains.' : '.'}
                  </div>
                ) : null}
                {added.length > 0 ? (
                  <div className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">
                    Will add: {added.join(', ')}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </MastersCard>

      <MastersCard
        id="masters-section-roleMaster"
        title="Role Master"
        description="Edit role cards used by Masters policies. Developer and Administrator are protected from removal."
        actions={
          <>
            <ResetButton saving={savingKey === 'roleTemplates'} onClick={() => void resetPolicy('roleTemplates')} />
            <SaveButton saving={savingKey === 'roleTemplates'} onClick={() => void savePolicy('roleTemplates', roleTemplates)} />
          </>
        }
      >
        <div className="mb-4">
          <button
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-slate-900 text-white dark:bg-white dark:text-slate-900"
            onClick={addRole}
          >
            Add Role
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {roleTemplates.map((role, index) => (
            <div key={role.id} className="border border-border rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-xs text-muted-foreground">Role ID</div>
                  <div className="font-mono text-xs bg-muted rounded px-2 py-1 mt-1">{role.id}</div>
                </div>

                {!['developer', 'admin', 'software_tester'].includes(role.id) && (
                  <button
                    className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/30"
                    onClick={() => removeRole(role.id)}
                  >
                    Remove
                  </button>
                )}
              </div>

              <label className="block text-xs font-semibold text-muted-foreground mb-1">Display Label</label>
              <input
                className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm mb-3"
                value={role.label}
                onChange={(e) => updateRole(index, { label: e.target.value })}
              />

              <div className="mb-3">
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Role Color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="color"
                    value={HEX_COLOR_RE.test(role.color ?? '') ? role.color : '#64748b'}
                    onChange={(e) => updateRole(index, { color: e.target.value })}
                    className="h-9 w-12 rounded border border-border bg-background p-1"
                    title="Pick role color"
                  />
                  <input
                    value={role.color ?? '#64748b'}
                    onChange={(e) => updateRole(index, { color: e.target.value })}
                    placeholder="#f59e0b"
                    className="w-28 border border-border bg-background text-foreground rounded-lg px-2 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-full border"
                    style={{ backgroundColor: `${role.color ?? '#64748b'}22`, borderColor: `${role.color ?? '#64748b'}55`, color: role.color ?? '#64748b' }}
                  >
                    Preview
                  </span>
                </div>
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {ROLE_COLOR_SWATCHES.map((col) => (
                    <button
                      key={col}
                      type="button"
                      onClick={() => updateRole(index, { color: col })}
                      className={`w-5 h-5 rounded-full border-2 transition-all ${(role.color ?? '#64748b').toLowerCase() === col.toLowerCase() ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: col }}
                      title={col}
                    />
                  ))}
                </div>
              </div>

              <label className="block text-xs font-semibold text-muted-foreground mb-1">Description</label>
              <textarea
                className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm min-h-[80px]"
                value={role.description ?? ''}
                onChange={(e) => updateRole(index, { description: e.target.value })}
              />
            </div>
          ))}
        </div>
      </MastersCard>

      <MastersCard
        id="masters-section-permissionsMaster"
        title="Permissions Master / Capability-Based RBAC"
        description="Phase 1 stores the capability matrix in PostgreSQL and exposes /api/masters/permissions. Existing role checks remain active until later enforcement phases."
        actions={
          <>
            <ResetButton saving={savingKey === 'capabilityPermissions'} onClick={() => void resetCapabilityMatrix()} />
            <SaveButton saving={savingKey === 'capabilityPermissions'} onClick={() => void saveCapabilityMatrix()} />
          </>
        }
      >
        <div className="mb-4 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-2">Module</label>
            <input
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm mb-3"
              placeholder="Search modules..."
              value={capabilitySearch}
              onChange={(e) => setCapabilitySearch(e.target.value)}
            />

            <div className="max-h-[420px] overflow-auto border border-border rounded-xl divide-y divide-border">
              {filteredCapabilityModules.map((module) => (
                <button
                  key={module.id}
                  type="button"
                  className={[
                    'w-full text-left px-3 py-3 hover:bg-muted transition',
                    selectedCapability?.id === module.id ? 'bg-blue-50 dark:bg-blue-950/30' : '',
                  ].join(' ')}
                  onClick={() => setSelectedCapabilityModule(module.id)}
                >
                  <div className="font-semibold text-sm text-foreground">{module.label}</div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-0.5">{module.id}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            {selectedCapability ? (
              <>
                <div className="mb-3 rounded-xl bg-muted/60 p-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div>
                      <div className="text-sm font-bold text-foreground">{selectedCapability.label}</div>
                      <p className="text-xs text-muted-foreground mt-1">{selectedCapability.description}</p>
                    </div>
                    {capabilityUpdatedAt && (
                      <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                        Updated {fmtTs(capabilityUpdatedAt)}
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-2">
                    Runtime enforcement active: this matrix is now used by frontend canCapability() and backend requireCapability()/canCapability() checks for supported routes. Some legacy role and customer-scope guards remain intentionally layered for safety.
                  </p>
                </div>

                <div className="overflow-auto border border-border rounded-xl">
                  <table className="min-w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="sticky left-0 bg-muted text-left px-3 py-2 font-bold min-w-[220px]">Role</th>
                        <th className="text-center px-3 py-2 font-bold whitespace-nowrap">All</th>
                        {selectedCapability.capabilities.map((capability) => (
                          <th key={capability.id} className="text-center px-3 py-2 font-bold whitespace-nowrap" title={capability.description ?? capability.id}>
                            <div>{capability.label}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">{capability.id}</div>
                            {capability.dangerous && (
                              <div className="mt-1 text-[10px] text-red-600 dark:text-red-300">Sensitive</div>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {roleTemplates.map((role) => {
                        const roleCaps = new Set(capabilityPermissions[selectedCapability.id]?.[role.id] ?? []);
                        const selectableCapabilities = selectedCapability.capabilities.filter((capability) => !isSoftwareTesterProtectedCapability(role.id, capability.id));
                        const allChecked = selectableCapabilities.length > 0 && selectableCapabilities.every((capability) => roleCaps.has(capability.id));

                        return (
                          <tr key={role.id} className="border-t border-border">
                            <td className="sticky left-0 bg-white dark:bg-card px-3 py-3">
                              <div className="font-semibold">{role.label}</div>
                              <div className="text-xs text-muted-foreground font-mono">{role.id}</div>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={(e) => setAllCapabilities(selectedCapability.id, role.id, e.target.checked)}
                              />
                            </td>
                            {selectedCapability.capabilities.map((capability) => (
                              <td key={capability.id} className="px-3 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={!isSoftwareTesterProtectedCapability(role.id, capability.id) && roleCaps.has(capability.id)}
                                  disabled={isSoftwareTesterProtectedCapability(role.id, capability.id)}
                                  onChange={() => toggleCapability(selectedCapability.id, role.id, capability.id)}
                                  title={isSoftwareTesterProtectedCapability(role.id, capability.id) ? 'Software Tester cannot be granted delete/destructive capabilities' : `${role.label} / ${selectedCapability.label} / ${capability.label}`}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">No capability modules loaded.</div>
            )}
          </div>
        </div>
      </MastersCard>

      <MastersCard
        id="masters-section-tabVisibility"
        title="Tab Visibility Master"
        description="Which nav tabs each role sees, in one grid. Reads and writes the same 'Visible' capability as Permissions Master above — this is just a faster view for that one job. Save/Reset here saves the whole capability matrix, same as Permissions Master."
        actions={
          <>
            <ResetButton saving={savingKey === 'capabilityPermissions'} onClick={() => void resetCapabilityMatrix()} />
            <SaveButton saving={savingKey === 'capabilityPermissions'} onClick={() => void saveCapabilityMatrix()} />
          </>
        }
      >
        <div className="overflow-auto border border-border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="sticky left-0 bg-muted text-left px-3 py-2 font-bold min-w-[220px]">Tab</th>
                {roleTemplates.map((role) => (
                  <th key={role.id} className="text-center px-3 py-2 font-bold whitespace-nowrap">
                    {role.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TAB_VISIBILITY_ROWS.map((row) => (
                <tr key={row.tabId} className="border-t border-border">
                  <td className="sticky left-0 bg-white dark:bg-card px-3 py-3">
                    <div className="font-semibold">{row.label}</div>
                    <div className="text-xs text-muted-foreground font-mono">{row.moduleId}.{row.capability}</div>
                  </td>
                  {roleTemplates.map((role) => {
                    const checked = (capabilityPermissions[row.moduleId]?.[role.id] ?? []).includes(row.capability);
                    return (
                      <td key={role.id} className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCapability(row.moduleId, role.id, row.capability)}
                          title={`${role.label} / ${row.label}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </MastersCard>

      <MastersCard
        id="masters-section-playbookAccess"
        title="Playbook Access Master"
        description="Control who can execute discovered Logic App playbooks per customer tenant. Populate this list from ResponseOps Studio → Discover Playbooks, then assign roles/users here before allowing execution."
        actions={
          <>
            <button
              type="button"
              onClick={() => void loadPolicies()}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-muted disabled:opacity-60"
              disabled={playbookAccessSaving}
            >
              Refresh
            </button>
            <SaveButton saving={playbookAccessSaving} onClick={() => void savePlaybookAccessMaster()} />
          </>
        }
      >
        <div className="mb-4 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Customer</label>
            <select
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              value={playbookAccessCustomerFilter}
              onChange={(e) => setPlaybookAccessCustomerFilter(e.target.value)}
            >
              <option value="all">All customers</option>
              {Array.from(new Map(playbookAccessRows.map((row) => [row.customerId, row.customerName || row.customerId])).entries())
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Search</label>
            <input
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              placeholder="Search playbook, resource group, subscription, connector, description or resource ID..."
              value={playbookAccessFilter}
              onChange={(e) => setPlaybookAccessFilter(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Discovered Playbooks</div>
            <div className="text-2xl font-bold">{playbookAccessRows.length}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Visible Rows</div>
            <div className="text-2xl font-bold">{filteredPlaybookAccessRows.length}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Enabled Policies</div>
            <div className="text-2xl font-bold">{playbookAccessRows.filter((row) => playbookAccessDrafts[playbookAccessKey(row)]?.enabled !== false).length}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Customer Tenants</div>
            <div className="text-2xl font-bold">{new Set(playbookAccessRows.map((row) => row.customerId)).size}</div>
          </div>
        </div>

        {playbookAccessRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground">
            No discovered playbooks yet. Go to <strong>ResponseOps Studio → Discover Playbooks</strong>, choose a customer/subscription, and run discovery. The discovered Logic Apps will appear here for execution-access governance.
          </div>
        ) : (
          <div className="space-y-4 max-h-[720px] overflow-auto pr-1">
            {filteredPlaybookAccessRows.map((row) => {
              const key = playbookAccessKey(row);
              const draft = playbookAccessDrafts[key] ?? {
                enabled: row.enabled !== false,
                allowedRoles: row.allowedRoles ?? [],
                allowedUsersText: (row.allowedUsers ?? []).join('\n'),
              };
              const selectedRoles = new Set(draft.allowedRoles ?? []);
              const userOverrides = parseEmailLines(draft.allowedUsersText ?? '');

              return (
                <div key={key} className="rounded-xl border border-border p-4">
                  <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-sm font-bold text-foreground break-all">{row.workflowName}</h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${draft.enabled === false ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900' : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900'}`}>
                          {draft.enabled === false ? 'Execution disabled' : 'Execution governed'}
                        </span>
                        {row.state && <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">{row.state}</span>}
                        {row.triggerType && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900">{row.triggerType}</span>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground break-all">
                        Customer: <strong>{row.customerName || row.customerId}</strong> · Subscription: <span className="font-mono">{row.subscriptionId || '-'}</span> · RG: {row.resourceGroup || '-'}
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground break-all">{row.resourceId}</div>
                      {row.description && <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{row.description}</p>}
                    </div>
                    <div className="flex flex-col items-start xl:items-end gap-2 text-xs text-muted-foreground">
                      <label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Toggle checked={draft.enabled !== false} onChange={(checked) => updatePlaybookAccessDraft(row, { enabled: checked })} label={`Enable ${row.workflowName}`} />
                        Allow execution
                      </label>
                      <div>{accessSummary(row, draft)}</div>
                      {row.policyUpdatedAt && <div>Updated {fmtTs(row.policyUpdatedAt)}</div>}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-4">
                    <div>
                      <div className="text-xs font-bold text-muted-foreground mb-2">Allowed roles</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {roleTemplates.map((role) => (
                          <label key={role.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs hover:bg-muted/50">
                            <input
                              type="checkbox"
                              checked={selectedRoles.has(role.id)}
                              onChange={() => togglePlaybookAccessRole(row, role.id)}
                            />
                            <span className="font-semibold">{role.label}</span>
                            <span className="font-mono text-[10px] text-muted-foreground">{role.id}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(row.connectors ?? []).map((connector) => (
                          <span key={connector} className="px-2 py-1 rounded-full border border-border bg-muted text-[11px] text-muted-foreground">{connector}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground mb-2">Allowed user overrides</label>
                      <textarea
                        className="w-full min-h-[120px] rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono text-foreground"
                        value={draft.allowedUsersText}
                        onChange={(e) => updatePlaybookAccessDraft(row, { allowedUsersText: e.target.value })}
                        placeholder="one.user@customer.com\nsecond.user@kloudynet.com"
                      />
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Parsed overrides: {userOverrides.length || 0}. Leave blank to rely only on allowed roles.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredPlaybookAccessRows.length === 0 && (
              <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">No playbooks match the current filter.</div>
            )}
          </div>
        )}
      </MastersCard>

      <MastersCard
        id="masters-section-socAssignment"
        title="SOC Assignment Master"
        description="Control the customer-scoped SOC assignee roster used by incident assignment. The Incident assignment dropdown now uses this roster instead of querying the full customer directory."
        actions={
          <>
            {!selectedSocAssignmentIsMsspTenant && (
              <button
                type="button"
                onClick={() => void refreshSocAssignmentLighthouse()}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-300 hover:bg-blue-500/20 disabled:opacity-60"
                disabled={socAssignmentSaving || Boolean(socAssignmentVerifyingCustomerId) || socAssignmentCustomerFilter === 'all'}
                title={socAssignmentCustomerFilter === 'all' ? 'Select a single customer first' : 'Validate the roster against Azure Lighthouse delegated principals and groups'}
              >
                {socAssignmentVerifyingCustomerId ? 'Verifying Lighthouse…' : 'Refresh from Azure Lighthouse'}
              </button>
            )}
            <button
              type="button"
              onClick={() => void loadPolicies()}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-muted disabled:opacity-60"
              disabled={socAssignmentSaving || Boolean(socAssignmentVerifyingCustomerId)}
            >
              Refresh
            </button>
            <SaveButton saving={socAssignmentSaving} onClick={() => void saveSocAssignmentMaster()} />
          </>
        }
      >
        <div className="mb-4 rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50/70 dark:bg-blue-950/20 p-4 text-xs text-blue-800 dark:text-blue-200">
          <div className="font-bold text-sm">Permanent assignment guardrail</div>
          <p className="mt-1">Only enabled users with customer access in Users Master and <span className="font-mono">incidents.assign</span> in Permissions Master are shown to analysts. Direct API assignment is also blocked if the assignee is not eligible here.</p>
          <p className="mt-2">
            {selectedSocAssignmentIsMsspTenant
              ? 'This selected tenant is the Kloudynet/MSSP home tenant. Azure Lighthouse verification is not required; assignment remains governed by Users Master, Permissions Master, and this roster.'
              : 'Use Refresh from Azure Lighthouse for a selected customer to read Lighthouse registration assignments at subscription and configured resource-group scopes, expand managing-tenant groups through Microsoft Graph, and stamp each roster user as verified or not verified. If customer-side registration definitions are stale or unreadable, configure the principal and delegated scope registries below.'}
          </p>
        </div>

        <div className="mb-4 rounded-xl border border-border bg-muted/10 p-4">
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
            <div>
              <div className="text-sm font-bold text-foreground">MSSP / Lighthouse Principal Registry</div>
              <p className="mt-1 text-xs text-muted-foreground max-w-4xl">
                Configure the managing-tenant Entra object IDs that are used in Azure Lighthouse authorizations for this customer. This becomes the permanent fallback when registration definitions return 404 or when delegation is group-based and must be expanded from the MSSP tenant.
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                <span className="rounded-full border border-border bg-background px-2 py-1">Managing tenant: <span className="font-mono text-foreground">{socAssignmentMsspTenantId || 'not configured'}</span></span>
                <span className="rounded-full border border-border bg-background px-2 py-1">MSSP app: <span className="font-mono text-foreground">{socAssignmentMsspClientId || 'not configured'}</span></span>
                {selectedSocAssignmentCustomer && <span className="rounded-full border border-border bg-background px-2 py-1">Selected customer tenant: <span className="font-mono text-foreground">{selectedSocAssignmentCustomer.tenantId || 'not set'}</span></span>}
                {selectedSocAssignmentIsMsspTenant
                  ? <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-600 dark:text-emerald-300">MSSP Home Tenant</span>
                  : selectedSocAssignmentCustomer?.lighthouseMode && <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-1 text-blue-600 dark:text-blue-300">Customer uses Lighthouse mode</span>}
              </div>
            </div>
            <div className="text-xs text-muted-foreground xl:text-right">
              <div className="font-semibold text-foreground">Registry entries</div>
              <div className="text-2xl font-bold text-foreground">{filteredSocAssignmentLighthousePrincipals.length + filteredSocAssignmentLighthouseScopes.length}</div>
            </div>
          </div>

          {socAssignmentCustomerFilter === 'all' ? (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
              Select a single customer to add or edit MSSP Lighthouse principal object IDs.
            </div>
          ) : selectedSocAssignmentIsMsspTenant ? (
            <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200">
              This is the MSSP home tenant. Lighthouse principal registry and delegated scope registry are not applicable and have been disabled for this tenant.
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 xl:grid-cols-[1.4fr_160px_1fr_1fr_auto] gap-2">
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs font-mono"
                placeholder="Entra object ID for Lighthouse group/user/service principal"
                value={socAssignmentNewPrincipal.principalId}
                onChange={(e) => setSocAssignmentNewPrincipal((prev) => ({ ...prev, principalId: e.target.value }))}
              />
              <select
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                value={socAssignmentNewPrincipal.principalType}
                onChange={(e) => setSocAssignmentNewPrincipal((prev) => ({ ...prev, principalType: e.target.value }))}
              >
                <option value="group">Group</option>
                <option value="user">User</option>
                <option value="servicePrincipal">Service principal</option>
                <option value="unknown">Unknown</option>
              </select>
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                placeholder="Display name / roster hint"
                value={socAssignmentNewPrincipal.displayName}
                onChange={(e) => setSocAssignmentNewPrincipal((prev) => ({ ...prev, displayName: e.target.value }))}
              />
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                placeholder="Notes, e.g. L2 SOC group"
                value={socAssignmentNewPrincipal.notes}
                onChange={(e) => setSocAssignmentNewPrincipal((prev) => ({ ...prev, notes: e.target.value }))}
              />
              <button
                type="button"
                onClick={addSocAssignmentLighthousePrincipal}
                className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-xs font-semibold text-blue-600 dark:text-blue-300 hover:bg-blue-500/20"
              >
                Add Principal
              </button>
            </div>
          )}

          {!selectedSocAssignmentIsMsspTenant && filteredSocAssignmentLighthousePrincipals.length > 0 && (
            <div className="mt-4 space-y-2">
              {filteredSocAssignmentLighthousePrincipals.map((principal) => {
                const key = socAssignmentLighthousePrincipalKey(principal);
                const draft = socAssignmentLighthouseDrafts[key] ?? { enabled: principal.enabled !== false, principalType: principal.principalType || 'group', displayName: principal.displayName ?? '', notes: principal.notes ?? '' };
                return (
                  <div key={key} className="rounded-lg border border-border bg-background/70 p-3">
                    <div className="grid grid-cols-1 xl:grid-cols-[1fr_150px_1fr_1fr_auto_auto] gap-2 items-start">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Principal object ID</div>
                        <div className="mt-1 font-mono text-xs text-foreground break-all">{principal.principalId}</div>
                      </div>
                      <select
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                        value={draft.principalType}
                        onChange={(e) => updateSocAssignmentLighthouseDraft(principal, { principalType: e.target.value })}
                      >
                        <option value="group">Group</option>
                        <option value="user">User</option>
                        <option value="servicePrincipal">Service principal</option>
                        <option value="unknown">Unknown</option>
                      </select>
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                        value={draft.displayName}
                        onChange={(e) => updateSocAssignmentLighthouseDraft(principal, { displayName: e.target.value })}
                        placeholder="Display name"
                      />
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                        value={draft.notes}
                        onChange={(e) => updateSocAssignmentLighthouseDraft(principal, { notes: e.target.value })}
                        placeholder="Notes"
                      />
                      <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs hover:bg-muted/50">
                        <Toggle checked={draft.enabled !== false} onChange={(checked) => updateSocAssignmentLighthouseDraft(principal, { enabled: checked })} label="Principal enabled" />
                        Enabled
                      </label>
                      <button
                        type="button"
                        onClick={() => void deleteSocAssignmentLighthousePrincipal(principal)}
                        className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-600 dark:text-red-300 hover:bg-red-500/20 disabled:opacity-60"
                        disabled={socAssignmentSaving}
                      >
                        Delete
                      </button>
                    </div>
                    {principal.updatedAt && <div className="mt-2 text-[10px] text-muted-foreground">Updated {fmtTs(principal.updatedAt)} by {principal.updatedByEmail || 'system'}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>



        {!selectedSocAssignmentIsMsspTenant && socAssignmentCustomerFilter !== 'all' && (
          <div className="mb-4 rounded-xl border border-border bg-muted/10 p-4">
            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
              <div>
                <div className="text-sm font-bold text-foreground">Delegated Scope Registry</div>
                <p className="mt-1 text-xs text-muted-foreground max-w-4xl">
                  Add the subscription or resource-group scopes where Azure Lighthouse was delegated. This avoids noisy 404s when the customer is onboarded at resource-group scope instead of whole-subscription scope.
                </p>
              </div>
              <div className="text-xs text-muted-foreground xl:text-right">
                <div className="font-semibold text-foreground">Delegated scopes</div>
                <div className="text-2xl font-bold text-foreground">{filteredSocAssignmentLighthouseScopes.length}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 xl:grid-cols-[150px_1fr_1fr_1fr_1fr_auto] gap-2">
              <select
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                value={socAssignmentNewScope.scopeType}
                onChange={(e) => setSocAssignmentNewScope((prev) => ({ ...prev, scopeType: e.target.value }))}
              >
                <option value="resourceGroup">Resource group</option>
                <option value="subscription">Subscription</option>
              </select>
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs font-mono"
                placeholder="Subscription ID"
                value={socAssignmentNewScope.subscriptionId || selectedSocAssignmentCustomer?.subscription || ''}
                onChange={(e) => setSocAssignmentNewScope((prev) => ({ ...prev, subscriptionId: e.target.value }))}
              />
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                placeholder="Resource group name"
                value={socAssignmentNewScope.resourceGroup}
                onChange={(e) => setSocAssignmentNewScope((prev) => ({ ...prev, resourceGroup: e.target.value }))}
                disabled={socAssignmentNewScope.scopeType !== 'resourceGroup'}
              />
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                placeholder="Display name / scope hint"
                value={socAssignmentNewScope.displayName}
                onChange={(e) => setSocAssignmentNewScope((prev) => ({ ...prev, displayName: e.target.value }))}
              />
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                placeholder="Notes, e.g. Sentinel delegated RG"
                value={socAssignmentNewScope.notes}
                onChange={(e) => setSocAssignmentNewScope((prev) => ({ ...prev, notes: e.target.value }))}
              />
              <button
                type="button"
                onClick={addSocAssignmentLighthouseScope}
                className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-xs font-semibold text-blue-600 dark:text-blue-300 hover:bg-blue-500/20"
              >
                Add Scope
              </button>
            </div>
            {filteredSocAssignmentLighthouseScopes.length > 0 && (
              <div className="mt-4 space-y-2">
                {filteredSocAssignmentLighthouseScopes.map((scope) => {
                  const key = socAssignmentLighthouseScopeKey(scope);
                  const draft = socAssignmentLighthouseScopeDrafts[key] ?? { enabled: scope.enabled !== false, scopeType: scope.scopeType || 'subscription', subscriptionId: scope.subscriptionId ?? '', resourceGroup: scope.resourceGroup ?? '', displayName: scope.displayName ?? '', notes: scope.notes ?? '' };
                  return (
                    <div key={key} className="rounded-lg border border-border bg-background/70 p-3">
                      <div className="grid grid-cols-1 xl:grid-cols-[150px_1fr_1fr_1fr_1fr_auto_auto] gap-2 items-start">
                        <select
                          className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                          value={draft.scopeType}
                          onChange={(e) => updateSocAssignmentLighthouseScopeDraft(scope, { scopeType: e.target.value })}
                        >
                          <option value="resourceGroup">Resource group</option>
                          <option value="subscription">Subscription</option>
                        </select>
                        <input
                          className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs font-mono"
                          value={draft.subscriptionId}
                          onChange={(e) => updateSocAssignmentLighthouseScopeDraft(scope, { subscriptionId: e.target.value })}
                          placeholder="Subscription ID"
                        />
                        <input
                          className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                          value={draft.resourceGroup}
                          onChange={(e) => updateSocAssignmentLighthouseScopeDraft(scope, { resourceGroup: e.target.value })}
                          placeholder="Resource group"
                          disabled={draft.scopeType !== 'resourceGroup'}
                        />
                        <input
                          className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                          value={draft.displayName}
                          onChange={(e) => updateSocAssignmentLighthouseScopeDraft(scope, { displayName: e.target.value })}
                          placeholder="Display name"
                        />
                        <input
                          className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-xs"
                          value={draft.notes}
                          onChange={(e) => updateSocAssignmentLighthouseScopeDraft(scope, { notes: e.target.value })}
                          placeholder="Notes"
                        />
                        <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs hover:bg-muted/50">
                          <Toggle checked={draft.enabled !== false} onChange={(checked) => updateSocAssignmentLighthouseScopeDraft(scope, { enabled: checked })} label="Scope enabled" />
                          Enabled
                        </label>
                        <button
                          type="button"
                          onClick={() => void deleteSocAssignmentLighthouseScope(scope)}
                          className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-600 dark:text-red-300 hover:bg-red-500/20 disabled:opacity-60"
                          disabled={socAssignmentSaving}
                        >
                          Delete
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                        {scope.lastCheckedAt && <span>Last checked {fmtTs(scope.lastCheckedAt)}</span>}
                        {scope.lastResult && <span>Last result: {scope.lastResult}</span>}
                        {scope.updatedAt && <span>Updated {fmtTs(scope.updatedAt)} by {scope.updatedByEmail || 'system'}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {socAssignmentLighthouseResult && (
          <div className={`mb-4 rounded-xl border p-4 text-xs ${(socAssignmentLighthouseResult.status === 'verified' || socAssignmentLighthouseResult.status === 'not_applicable_mssp_home') ? 'border-emerald-300 bg-emerald-50/70 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200' : socAssignmentLighthouseResult.status === 'partial' ? 'border-amber-300 bg-amber-50/70 text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200' : 'border-red-300 bg-red-50/70 text-red-800 dark:border-red-900 dark:bg-red-950/20 dark:text-red-200'}`}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div>
                <div className="font-bold text-sm">Azure Lighthouse verification: {socAssignmentLighthouseResult.status}</div>
                <p className="mt-1">{socAssignmentLighthouseResult.message}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="rounded-full border border-current/20 px-2 py-1">Principals: {socAssignmentLighthouseResult.principalsDiscovered}</span>
                  <span className="rounded-full border border-current/20 px-2 py-1">Groups: {socAssignmentLighthouseResult.groupsExpanded}/{socAssignmentLighthouseResult.groupsDiscovered} expanded</span>
                  <span className="rounded-full border border-current/20 px-2 py-1">Graph users: {socAssignmentLighthouseResult.usersDiscovered}</span>
                  <span className="rounded-full border border-current/20 px-2 py-1">Verified: {socAssignmentLighthouseResult.verifiedUsers}</span>
                  <span className="rounded-full border border-current/20 px-2 py-1">Unverified: {socAssignmentLighthouseResult.unverifiedUsers}</span>
                  <span className="rounded-full border border-current/20 px-2 py-1">Azure discovered: {socAssignmentLighthouseResult.discoveredPrincipalsUsed ?? socAssignmentLighthouseResult.principalsDiscovered}</span>
                  <span className="rounded-full border border-current/20 px-2 py-1">Configured registry: {socAssignmentLighthouseResult.configuredPrincipalsUsed ?? 0}</span>
                  <span className="rounded-full border border-current/20 px-2 py-1">Source: {socAssignmentLighthouseResult.verificationSource || 'azure_lighthouse'}</span>
                  {typeof socAssignmentLighthouseResult.scopesChecked === 'number' && <span className="rounded-full border border-current/20 px-2 py-1">Scopes checked: {socAssignmentLighthouseResult.scopesChecked}</span>}
                  {typeof socAssignmentLighthouseResult.resourceGroupScopesChecked === 'number' && <span className="rounded-full border border-current/20 px-2 py-1">RG scopes: {socAssignmentLighthouseResult.resourceGroupScopesChecked}</span>}
                </div>
              </div>
              <button type="button" className="self-start rounded-lg border border-current/20 px-3 py-1.5 text-xs font-semibold" onClick={() => setSocAssignmentLighthouseResult(null)}>Dismiss</button>
            </div>
            {socAssignmentLighthouseResult.warnings?.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer font-semibold">Warnings / permission notes</summary>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {socAssignmentLighthouseResult.warnings.slice(0, 8).map((warning, index) => <li key={index}>{warning}</li>)}
                </ul>
              </details>
            )}
            {(socAssignmentLighthouseResult.technicalDetails?.length ?? 0) > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer font-semibold">Technical details</summary>
                <ul className="mt-2 list-disc pl-5 space-y-1 font-mono text-[11px]">
                  {(socAssignmentLighthouseResult.technicalDetails ?? []).slice(0, 12).map((detail, index) => <li key={index}>{detail}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Customer</label>
            <select
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              value={socAssignmentCustomerFilter}
              onChange={(e) => setSocAssignmentCustomerFilter(e.target.value)}
            >
              <option value="all">All customers</option>
              {socAssignmentCustomers
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Search</label>
            <input
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              placeholder="Search SOC user, email, role, customer, reason or notes..."
              value={socAssignmentFilter}
              onChange={(e) => setSocAssignmentFilter(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">SOC Users in Scope</div>
            <div className="text-2xl font-bold">{socAssignmentRows.length}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Eligible Assignees</div>
            <div className="text-2xl font-bold">{socAssignmentRows.filter((row) => (socAssignmentDrafts[socAssignmentKey(row)]?.enabled ?? row.enabled) !== false && (socAssignmentDrafts[socAssignmentKey(row)]?.canAssign ?? row.canAssign) !== false && row.portalCustomerAccess && row.hasAssignCapability).length}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Auto-Assign Ready</div>
            <div className="text-2xl font-bold">{socAssignmentRows.filter((row) => socAssignmentDrafts[socAssignmentKey(row)]?.autoAssign ?? row.autoAssign).length}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Lighthouse Verified</div>
            <div className="text-2xl font-bold">{socAssignmentRows.filter((row) => socAssignmentDrafts[socAssignmentKey(row)]?.lighthouseVerified ?? row.lighthouseVerified).length}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Customer Tenants</div>
            <div className="text-2xl font-bold">{new Set(socAssignmentRows.map((row) => row.customerId)).size}</div>
          </div>
        </div>

        {socAssignmentRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground">
            No SOC assignee candidates found. Add SOC users in Users Master and give them customer access plus the <span className="font-mono">incidents.assign</span> capability.
          </div>
        ) : (
          <div className="space-y-4 max-h-[760px] overflow-auto pr-1">
            {filteredSocAssignmentRows.map((row) => {
              const key = socAssignmentKey(row);
              const draft = socAssignmentDrafts[key] ?? {
                enabled: row.enabled !== false,
                canAssign: row.canAssign !== false,
                autoAssign: row.autoAssign === true,
                allowServicePrincipalFallback: row.allowServicePrincipalFallback === true,
                severityScopes: row.severityScopes?.length ? row.severityScopes : ['Any'],
                lighthouseVerified: row.lighthouseVerified === true,
                notes: row.notes ?? '',
              };
              const severitySet = new Set(draft.severityScopes ?? ['Any']);
              const statusText = row.portalCustomerAccess
                ? row.hasAssignCapability
                  ? (draft.enabled !== false && draft.canAssign !== false ? 'Eligible' : 'Disabled')
                  : 'Missing incidents.assign'
                : 'No customer access';
              return (
                <div key={key} className="rounded-xl border border-border bg-muted/10 p-4">
                  <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-bold text-sm text-foreground">{row.displayName || row.userEmail}</div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-border bg-muted text-muted-foreground font-mono">{row.role}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusText === 'Eligible' ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300' : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300'}`}>{statusText}</span>
                        {draft.autoAssign && <span className="text-[10px] px-2 py-0.5 rounded-full border border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900 dark:bg-purple-950/30 dark:text-purple-300">Auto-assign</span>}
                        {draft.allowServicePrincipalFallback && <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">SPN fallback</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${selectedSocAssignmentIsMsspTenant ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300' : draft.lighthouseVerified ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300' : 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-950/30 dark:text-slate-300'}`}>{selectedSocAssignmentIsMsspTenant ? 'Lighthouse not applicable' : draft.lighthouseVerified ? 'Lighthouse verified' : 'Lighthouse not verified'}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground break-all">
                        <span className="font-mono">{row.userEmail}</span> · Customer: <strong>{socAssignmentCustomers.find((c) => c.id === row.customerId)?.name || row.customerId}</strong>
                      </div>
                      {row.reason && <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">{row.reason}</div>}
                      {row.lastVerifiedAt && <div className="mt-1 text-[11px] text-sky-600 dark:text-sky-300">Lighthouse checked {fmtTs(row.lastVerifiedAt)}</div>}
                      {row.updatedAt && <div className="mt-1 text-[11px] text-muted-foreground">Updated {fmtTs(row.updatedAt)} by {row.updatedByEmail || 'system'}</div>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 text-xs">
                      <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 hover:bg-muted/50">
                        <Toggle checked={draft.enabled !== false} onChange={(checked) => updateSocAssignmentDraft(row, { enabled: checked })} label="Enabled" />
                        Enabled
                      </label>
                      <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 hover:bg-muted/50">
                        <Toggle checked={draft.canAssign !== false} onChange={(checked) => updateSocAssignmentDraft(row, { canAssign: checked })} label="Can assign" />
                        Can assign
                      </label>
                      <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 hover:bg-muted/50">
                        <Toggle checked={draft.autoAssign === true} onChange={(checked) => updateSocAssignmentDraft(row, { autoAssign: checked })} label="Auto assign" />
                        Auto-assign
                      </label>
                      <label className="flex items-center gap-2 rounded-lg border border-amber-200/70 px-3 py-2 hover:bg-amber-50/50 dark:border-amber-900 dark:hover:bg-amber-950/20" title="Allows this analyst to explicitly approve service-principal execution only after their delegated Lighthouse action fails.">
                        <Toggle checked={draft.allowServicePrincipalFallback === true} onChange={(checked) => updateSocAssignmentDraft(row, { allowServicePrincipalFallback: checked })} label="SPN incident fallback" />
                        SPN fallback
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-4">
                    <div>
                      <div className="text-xs font-bold text-muted-foreground mb-2">Severity scope</div>
                      <div className="flex flex-wrap gap-2">
                        {ASSIGNMENT_SEVERITY_OPTIONS.map((severity) => (
                          <label key={severity} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs hover:bg-muted/50">
                            <input
                              type="checkbox"
                              checked={severitySet.has(severity)}
                              onChange={() => toggleSocAssignmentSeverity(row, severity)}
                            />
                            {severity}
                          </label>
                        ))}
                      </div>
                      {!selectedSocAssignmentIsMsspTenant && (
                        <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={draft.lighthouseVerified === true}
                            onChange={(e) => updateSocAssignmentDraft(row, { lighthouseVerified: e.target.checked })}
                          />
                          Mark Lighthouse access verified manually
                        </label>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground mb-2">Notes / verification evidence</label>
                      <textarea
                        className="w-full min-h-[100px] rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
                        value={draft.notes}
                        onChange={(e) => updateSocAssignmentDraft(row, { notes: e.target.value })}
                        placeholder="Optional: Lighthouse group name, on-call pool, tenant access note, escalation restriction..."
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredSocAssignmentRows.length === 0 && (
              <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">No assignees match the current filter.</div>
            )}
          </div>
        )}
      </MastersCard>

      <MastersCard
        id="masters-section-kqlTableAccess"
        title="KQL Table Access Master"
        description="Select a role, choose all-catalog or selected-table mode, and manage denied tables."
        actions={
          <>
            <ResetButton saving={savingKey === 'kqlTablePolicies'} onClick={() => void resetPolicy('kqlTablePolicies')} />
            <SaveButton saving={savingKey === 'kqlTablePolicies'} onClick={() => void savePolicy('kqlTablePolicies', kqlPolicies)} />
          </>
        }
      >
        <div className="mb-4 rounded-xl border border-border bg-muted/20 p-4">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Global Table Catalog</div>
              <p className="mt-1 text-xs text-muted-foreground">Add legitimate Sentinel / Log Analytics / Defender table names here when a feature warns that a table is blocked by the global allowlist. After saving, grant role access below.</p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  className="w-full sm:max-w-sm border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                  placeholder="Add table, e.g. Usage or CustomTable_CL"
                  value={customKqlTableDraft}
                  onChange={(e) => setCustomKqlTableDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomKqlTable(); } }}
                />
                <button type="button" className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted" onClick={addCustomKqlTable}>Add Table</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <ResetButton saving={savingKey === 'kqlTableCatalog'} onClick={() => void resetPolicy('kqlTableCatalog')} />
              <SaveButton saving={savingKey === 'kqlTableCatalog'} onClick={() => void savePolicy('kqlTableCatalog', { customTables: customKqlTables })} />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {customKqlTables.length === 0 ? (
              <span className="text-xs text-muted-foreground">No custom catalog tables added. Built-in catalog tables still appear below.</span>
            ) : (
              customKqlTables.map((table) => (
                <button
                  key={table}
                  type="button"
                  className="px-2 py-1 rounded-full text-xs bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900"
                  onClick={() => removeCustomKqlTable(table)}
                  title="Click to remove from custom catalog draft"
                >
                  {table} ×
                </button>
              ))
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-2">Role</label>
            <select
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              value={selectedKqlRole}
              onChange={(e) => setSelectedKqlRole(e.target.value)}
            >
              {roleTemplates.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label} ({role.id})
                </option>
              ))}
            </select>

            {selectedRole && (
              <div className="mt-4 rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">
                <div className="font-semibold text-foreground">{selectedRole.label}</div>
                <div className="font-mono mt-1">{selectedRole.id}</div>
                {selectedRole.description && <p className="mt-2">{selectedRole.description}</p>}
              </div>
            )}

            <div className="mt-4 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={selectedMode === 'allow_all_catalog'}
                  onChange={() => selectedKqlRole && setRoleMode(selectedKqlRole, 'allow_all_catalog')}
                />
                Allow all tables in global catalog
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={selectedMode !== 'allow_all_catalog'}
                  onChange={() => selectedKqlRole && setRoleMode(selectedKqlRole, 'selected')}
                />
                Selected tables only
              </label>
            </div>
          </div>

          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <input
                className="w-full sm:max-w-md border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                placeholder="Search tables..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
              />

              <div className="text-xs text-muted-foreground">
                {selectedMode === 'allow_all_catalog'
                  ? `All ${kqlTables.length} catalog tables allowed unless denied`
                  : `${selectedAllowed.size} allowed of ${kqlTables.length}`}
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs font-semibold text-muted-foreground mb-2">Denied Table Chips</div>
              <div className="flex flex-wrap gap-2">
                {Array.from(selectedDenied).length === 0 ? (
                  <span className="text-xs text-muted-foreground">No denied tables configured.</span>
                ) : (
                  Array.from(selectedDenied).sort().map((table) => (
                    <button
                      key={table}
                      className="px-2 py-1 rounded-full text-xs bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-100 dark:border-red-900"
                      onClick={() => selectedKqlRole && removeDeniedTable(selectedKqlRole, table)}
                      title="Click to remove from denied list"
                    >
                      {table} ×
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="border border-border rounded-xl overflow-hidden">
              <div className="grid grid-cols-[1fr_120px_90px] bg-muted px-3 py-2 text-xs font-bold text-muted-foreground">
                <div>Table</div>
                <div className="text-center">Allowed</div>
                <div className="text-center">Deny</div>
              </div>

              <div className="max-h-[460px] overflow-auto divide-y divide-border">
                {filteredKqlTables.map((table) => {
                  const isDenied = selectedDenied.has(table);
                  const isAllowed = selectedMode === 'allow_all_catalog' || selectedAllowed.has(table);

                  return (
                    <div key={table} className="grid grid-cols-[1fr_120px_90px] items-center px-3 py-2 text-sm">
                      <div className="font-mono text-xs break-all">{table}</div>

                      <div className="text-center">
                        <input
                          type="checkbox"
                          disabled={selectedMode === 'allow_all_catalog' || isDenied}
                          checked={isAllowed && !isDenied}
                          onChange={() => selectedKqlRole && toggleAllowedTable(selectedKqlRole, table)}
                        />
                      </div>

                      <div className="text-center">
                        {isDenied ? (
                          <button
                            className="text-xs px-2 py-1 rounded border border-red-200 text-red-600"
                            onClick={() => selectedKqlRole && removeDeniedTable(selectedKqlRole, table)}
                          >
                            Undo
                          </button>
                        ) : (
                          <button
                            className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
                            onClick={() => selectedKqlRole && addDeniedTable(selectedKqlRole, table)}
                          >
                            Deny
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </MastersCard>

      <MastersCard
        id="masters-section-reportAccess"
        title="Report Access Master"
        description="Configure which report actions are available to each role."
        actions={
          <>
            <ResetButton saving={savingKey === 'reportAccess'} onClick={() => void resetPolicy('reportAccess')} />
            <SaveButton saving={savingKey === 'reportAccess'} onClick={() => void savePolicy('reportAccess', reportAccess)} />
          </>
        }
      >
        <div className="overflow-auto border border-border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Role</th>
                {REPORT_LABELS.map((item) => (
                  <th key={item.key} className="text-center px-3 py-2 font-bold whitespace-nowrap">
                    {item.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roleTemplates.map((role) => (
                <tr key={role.id} className="border-t border-border">
                  <td className="px-3 py-3">
                    <div className="font-semibold">{role.label}</div>
                    <div className="text-xs text-muted-foreground font-mono">{role.id}</div>
                  </td>

                  {REPORT_LABELS.map((item) => (
                    <td key={item.key} className="px-3 py-3 text-center">
                      <Toggle
                        checked={Boolean(reportAccess[role.id]?.[item.key])}
                        onChange={() => toggleReportAccess(role.id, item.key)}
                        label={`${role.label} ${item.label}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </MastersCard>

      <MastersCard
        id="masters-section-featureFlags"
        title="Feature Flags"
        description="Turn platform modules and sensitive capabilities on or off globally."
        actions={
          <>
            <ResetButton saving={savingKey === 'featureFlags'} onClick={() => void resetPolicy('featureFlags')} />
            <SaveButton saving={savingKey === 'featureFlags'} onClick={() => void savePolicy('featureFlags', featureFlags)} />
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Object.keys(featureFlags).sort().map((key) => (
            <div key={key} className="border border-border rounded-xl p-4 flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-sm">
                  {FEATURE_LABELS[key] ?? key}
                </div>
                <div className="text-xs text-muted-foreground font-mono mt-1">{key}</div>
              </div>

              <Toggle
                checked={Boolean(featureFlags[key])}
                onChange={() => toggleFeatureFlag(key)}
                label={FEATURE_LABELS[key] ?? key}
              />
            </div>
          ))}
        </div>
      </MastersCard>
      <div id="masters-section-notifications" className="scroll-mt-24">
        <MastersBannerNotifications />
      </div>
    </div>
  );
}
